/**
 * SCI Field QC App — shared type contract.
 *
 * THIS FILE IS THE CONTRACT between the Hono/Workers API and the Svelte
 * frontend. Both sides import from here. Nothing else is negotiable.
 *
 * Conventions
 * -----------
 * 1. Entity interfaces mirror the D1 tables in ./../../schema.sql exactly,
 *    field-for-field, in snake_case. The API returns D1 rows unmapped.
 * 2. All JSON payloads use snake_case, with ONE deliberate exception:
 *    `Session` uses camelCase because the spec mandates that shape.
 * 3. `number | null` means the column is nullable. Absent (`?`) in a request
 *    body means "leave unchanged"; explicit `null` means "clear this value".
 * 4. Timestamps are ISO-8601 UTC strings set by the server. Dates are
 *    'YYYY-MM-DD'. Clock times may be 'HH:MM' or ISO-8601.
 * 5. SQLite booleans cross the wire as 0 | 1, never true/false.
 */

// The exceptions to "no imports": each is the actual return shape of a pure,
// already-tested calc function, and duplicating its fields here would only
// let the two drift.
import type { ReconcileResult } from './statements/reconcile';
import type { ContractLine } from './calc/contract';

/* ==========================================================================
 * Primitives and enums
 * ======================================================================== */

/** SQLite boolean. 0 = no, 1 = yes, null = not answered yet. */
export type SqlBool = 0 | 1;

/** ISO-8601 UTC timestamp, e.g. '2026-07-28T14:03:11.412Z'. */
export type Timestamp = string;

/** Calendar date, 'YYYY-MM-DD'. */
export type DateString = string;

/** 'HH:MM' or an ISO-8601 timestamp. */
export type TimeString = string;

/**
 * The three roles. No per-person accounts exist.
 *
 *   crew   — field QC. Reads and writes the pour record.
 *   client — field QC, read only.
 *   office — the mix design library, and NOTHING else. An office session
 *            cannot read a pour, and a crew session cannot read a mix design.
 *            Both refusals are server-side; see roleSatisfies in
 *            ./api-contract.ts.
 */
export type Role = 'crew' | 'client' | 'office';

/**
 * What a company account is FOR. An account reaches its area's routes and no
 * others, so adding a set of forms means adding an area, not widening a role.
 */
export type AccountArea = 'mix_designs' | 'quality' | 'admin';

/** Human labels for each area, for the app bar and the account list. */
export const ACCOUNT_AREA_LABELS: Record<AccountArea, string> = {
  mix_designs: 'Mix designs',
  quality: 'Quality forms',
  admin: 'Admin'
};

/** Pour lifecycle. */
export type PourStatus = 'draft' | 'open' | 'closed';

/** ITP / job-setup item status. 'na' is the paper's "N-A". */
export type ItpStatus = 'pending' | 'done' | 'na' | 'failed';

/** End-of-pour checklist status (Appendix B is Yes / No / N-A). */
export type EndOfPourStatus = 'pending' | 'yes' | 'no' | 'na';

/** N/A, OK, or Requires Repair, per the printed legend. */
export type InspectionStatus = 'pending' | 'na' | 'ok' | 'rr';

export const INSPECTION_STATUS_LABELS: Record<InspectionStatus, string> = {
  pending: '—',
  na: 'N/A',
  ok: 'OK',
  rr: 'RR'
};

/**
 * The five printed groups of the Equipment Safety Inspection checklist, in
 * printed order. See EQUIPMENT_CHECKLIST in ./seed-data.ts for the items.
 */
export type EquipmentChecklistGroup = 'outside' | 'engine' | 'cab' | 'cab_continued' | 'fluids';

/**
 * ITP point type. 'H' = Hold, 'W' = Witness, 'S' = Surveillance, 'R' = Review.
 * Combinations are slash-joined, e.g. 'H/R'. Anything containing 'H' is a
 * Hold point and is subject to the blocking rule.
 */
export type PointType = string;

/** Where in the pour a chute test was taken. */
export type ChuteTestPoint =
  | 'initial'
  | 'interval'
  | 'after_adjustment'
  | 'after_stop_restart'
  | 'visible_change'
  | 'final';

/** Human labels for ChuteTestPoint, for pickers and exports. */
export const CHUTE_TEST_POINT_LABELS: Record<ChuteTestPoint, string> = {
  initial: 'Initial / start-up',
  interval: 'Interval',
  after_adjustment: 'After adjustment',
  after_stop_restart: 'After stop / restart',
  visible_change: 'Visible change',
  final: 'Final'
};

/** Section a photo can be attached to. */
export type PhotoSection =
  | 'job_setup'
  | 'header'
  | 'material_loading'
  | 'calibration'
  | 'calibration_trial'
  | 'itp_item'
  | 'chute_test'
  | 'stop_hold_event'
  | 'end_of_pour'
  | 'general';

/** Audit actions. The log is append-only. */
export type AuditAction = 'create' | 'update' | 'delete';

/* ==========================================================================
 * Entities — one interface per D1 table
 * ======================================================================== */

export interface Project {
  id: number;
  code: string;
  name: string;
  client: string;
  /** Never sent to the frontend. Present here because the API row type needs it. */
  crew_password_hash: string;
  /** Never sent to the frontend. */
  client_password_hash: string;
  archived: SqlBool;
  product: string | null;
  sci_unit_no: string | null;
  acceptance_point: string | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/** Project as returned by the API — password hashes stripped. */
export type PublicProject = Omit<Project, 'crew_password_hash' | 'client_password_hash'>;

export interface ShareToken {
  id: number;
  project_id: number;
  token: string;
  label: string;
  created_at: Timestamp;
  revoked_at: Timestamp | null;
}

/**
 * A company-wide login, scoped to one area. Unlike `Project`, an account is
 * not tied to a job — it names the area of the app it opens (today, only the
 * mix design library).
 */
export interface Account {
  id: number;
  login: string;
  /** Never sent to the frontend. */
  password_hash: string;
  area: AccountArea;
  label: string;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/** Unit of a materials-table row. '%' exists for the Air Content row. */
export type MixMaterialUnit = 'kg' | 'L' | 'mL' | '%';

/**
 * One authored mix design sheet.
 *
 * Company-wide, not project-scoped: `project_name` is free text on the form,
 * not a foreign key. Slump and strength are free text because the sheet mixes
 * a value with a tolerance ('180+/-20 mm', '5 MPa @ 7 days'). Nothing here
 * carries a verdict.
 */
export interface MixDesign {
  id: number;
  project_name: string;
  mix_code: string;
  subject: string;
  slump: string;
  specified_strength: string;
  wc_ratio: number | null;
  wc_ratio_max: number | null;
  yield_m3: number;
  notes: string;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/**
 * One row of the materials table.
 *
 * `amount` is a string, not a number: the Air Content row is a range ('1-4').
 * The mass total is computed from the rows that parse as a plain number in kg
 * or L — see mixMassTotalKg in ./calc/mix-design.ts.
 */
export interface MixDesignMaterial {
  id: number;
  mix_design_id: number;
  seq: number;
  name: string;
  amount: string;
  unit: MixMaterialUnit;
  density_kg_m3: number | null;
  volume_m3: number | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/** A mix design with its material rows, in seq order. */
export interface MixDesignFull {
  mix_design: MixDesign;
  materials: MixDesignMaterial[];
}

/** List-row shape: enough to identify a design without loading its rows. */
export interface MixDesignSummary {
  id: number;
  project_name: string;
  mix_code: string;
  subject: string;
  material_count: number;
  updated_at: Timestamp;
}

export interface JobSetup {
  id: number;
  project_id: number;
  water_source: string | null;
  potable_water_doc: string | null;
  pump_line_setup: string | null;
  acceptance_point: string | null;
  manufacturer_confirmation: string | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/** ITP items 1..5 (Appendix A, job-setup half). */
export interface JobSetupItem {
  id: number;
  job_setup_id: number;
  /** 1..5 */
  item_no: number;
  title: string;
  point_type: PointType;
  status: ItpStatus;
  initials: string | null;
  recorded_at: Timestamp | null;
  notes: string | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export interface Pour {
  id: number;
  project_id: number;
  /** Free text, e.g. 'Pile 1'. */
  label: string;
  date: DateString;
  status: PourStatus;
  closed_at: Timestamp | null;
  closed_by: string | null;
  created_by: string | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export interface PourHeader {
  id: number;
  pour_id: number;
  pour_location: string | null;
  sci_unit_no: string | null;
  operator: string | null;
  feed_water_control_operator: string | null;
  pump_operator: string | null;
  kiewit_qc_tester: string | null;
  water_source: string | null;
  material_lot_no: string | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export interface MaterialLoading {
  id: number;
  pour_id: number;
  product_name: string | null;
  lot_batch_no: string | null;
  supersack_numbers: string | null;
  /** kg */
  quantity_loaded: number | null;
  packaging_condition: string | null;
  storage_condition: string | null;
  loaded_by: string | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/**
 * The 10 pre-pour calibration items, plus the sheet-level values of the
 * calibration worksheet (prefixed `trial_`) that the three CalibrationTrial
 * rows sit inside.
 */
export interface PrePourCalibration {
  id: number;
  pour_id: number;
  approved_water_dosage: number | null;
  dry_feed_setting: number | null;
  water_flow_meter_setting: number | null;
  /** m3/h */
  approved_production_rate: number | null;
  material_feed_stable: SqlBool | null;
  water_input_stable: SqlBool | null;
  /** degC */
  temp_ambient: number | null;
  /** degC */
  temp_water: number | null;
  /** degC */
  temp_grout: number | null;
  pump_line_readiness: string | null;
  acceptance_point: string | null;
  stop_hold_contacts: string | null;
  /** kg per count actually adopted for this pour */
  trial_wt_per_count_used: number | null;
  /** water check: litres dispensed */
  trial_water_litres: number | null;
  /** water check: weight in kg (expect litres/kg within +/-1%) */
  trial_water_kg: number | null;
  trial_test_start: TimeString | null;
  trial_test_end: TimeString | null;
  /** degC */
  trial_temp_material: number | null;
  /** degC */
  trial_temp_ambient: number | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/**
 * Three rows per pour. `reading_kg / counts` gives wt/count.
 *
 * A trial is a RECORD ONLY: there is nothing to judge it against. The
 * computer-vs-actual verification is a separate activity with its own two rows
 * (see `CalibrationCheck`) — three trials calibrate, then two checks verify.
 * Until 2026-07-29 this row also carried `check_counts` and `actual_kg`, which
 * implied three checks and made the trials block show a verdict it could never
 * compute.
 *
 * Trials calibrate THE MIX, not the discharge. Their wt/counts therefore differ
 * from each other, sometimes widely, and that spread is normal — it is not
 * evidence of a fault and must never be flagged as one, nor turned into a
 * pass/fail. What the mixer is actually putting out at a moment in time is
 * recorded in `CalibrationCheck`, and that is the only thing judged. (Geoff,
 * 2026-08-04, correcting exactly this misreading: trial 1 at 0.069 against
 * trial 2 at 0.079 on the live pour is expected, not a typo.)
 *
 * `wt_per_count_mean` exists as the Appendix C step 7 back-check for a human to
 * eyeball. Do not add a cross-trial consistency rule on top of it.
 */
export interface CalibrationTrial {
  id: number;
  pour_id: number;
  /** 1..3 */
  trial_no: number;
  reading_kg: number | null;
  counts: number | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/**
 * Two rows per pour: the computer-vs-actual verification that follows
 * calibration (workbook columns E-H, "Check 1 / Actual 1 / Check 2 / Actual 2").
 *
 * The Check side is entered by hand in full — none of it is derived, and it is
 * not prefilled from the adopted wt/count, so a mid-pour recalibration can be
 * recorded honestly. `check_counts` and `check_kg` are kept for fidelity with
 * the paperwork and are deliberately NOT inputs to any calculation: in the real
 * worksheet `52 / 706` is 0.0736 while the wt/count typed above it is 0.076, so
 * the three Check figures are not internally consistent and must not be made to
 * look as though they are.
 *
 * The Actual side is measured; its wt/count is derived from the two readings.
 */
export interface CalibrationCheck {
  id: number;
  pour_id: number;
  /** 1..2 */
  check_no: number;
  check_counts: number | null;
  check_kg: number | null;
  /** The reference the variance is judged against. Entered, never derived. */
  check_wt_per_count: number | null;
  actual_counts: number | null;
  actual_kg: number | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/** ITP items 6..31 (Appendix A, per-pour half). */
export interface ItpItem {
  id: number;
  pour_id: number;
  /** 6..31 */
  item_no: number;
  title: string;
  point_type: PointType;
  status: ItpStatus;
  initials: string | null;
  recorded_at: Timestamp | null;
  notes: string | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export interface ChuteTest {
  id: number;
  pour_id: number;
  /** 1-based display order within the pour */
  seq: number;
  test_point: ChuteTestPoint;
  approx_volume_m3: number | null;
  sample_time: TimeString | null;
  /** INCHES. Target 16-19. The source workbook's "mm" label is an error. */
  spread_in: number | null;
  /** seconds. Target 40-70. */
  flow_seconds: number | null;
  /** lb/ft3. Captured with NO verdict until acceptance limits are supplied. */
  mud_balance: number | null;
  grout_temp_c: number | null;
  water_temp_c: number | null;
  pile: string | null;
  mix_no: string | null;
  strength_mpa: number | null;
  counts: number | null;
  grout_wt_kg: number | null;
  water_vol_l: number | null;
  /** Computed and stored server-side: water_vol_l / grout_wt_kg. */
  wc_ratio: number | null;
  accepted: SqlBool | null;
  tester_initials: string | null;
  notes: string | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export interface StopHoldEvent {
  id: number;
  pour_id: number;
  /** 1-based within the pour */
  event_no: number;
  time_stopped: TimeString | null;
  reason: string | null;
  corrective_action: string | null;
  additional_test_required: SqlBool | null;
  restart_time: TimeString | null;
  qc_acceptance_notes: string | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/** The 14 Yes/No/N-A items of Appendix B. */
export interface EndOfPourItem {
  id: number;
  pour_id: number;
  /** 1..14 */
  item_no: number;
  title: string;
  status: EndOfPourStatus;
  notes: string | null;
  initials: string | null;
  recorded_at: Timestamp | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export interface Photo {
  id: number;
  project_id: number;
  /** null for job-setup photos (section === 'job_setup') */
  pour_id: number | null;
  section: PhotoSection | null;
  /** id of the row within `section`, when relevant */
  row_id: number | null;
  r2_key: string;
  caption: string | null;
  taken_at: Timestamp | null;
  uploaded_by: string | null;
  width: number | null;
  height: number | null;
  bytes: number | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/** Append-only. Never updated, never deleted. The evidentiary backbone. */
export interface AuditEntry {
  id: number;
  project_id: number;
  pour_id: number | null;
  /** shift name chosen at session start */
  actor: string;
  at: Timestamp;
  action: AuditAction;
  /** e.g. 'chute_test:42', 'pour:7', 'itp_item:311' */
  target: string;
  /** JSON snapshot before the change, or null on create */
  before: string | null;
  /** JSON snapshot after the change, or null on delete */
  after: string | null;
  created_at: Timestamp;
}

/* ==========================================================================
 * Entities — the quality area
 *
 * Four digitised paper forms, behind the `sciquality` login. Company-wide,
 * not project-scoped: project, location and job number are free text on each
 * form, exactly as on paper. No foreign key to `project`. No verdicts, no
 * acceptance logic, no audit entries — these are records of what was
 * observed and decided, not measurements the app judges. See
 * docs/superpowers/specs/2026-08-22-quality-area-design.md.
 * ======================================================================== */

/** Job Hazard Analysis header. */
export interface Jha {
  id: number;
  /** The job this belongs to (Kits, Kingsway, Demo). The list groups by it. */
  jobsite: string;
  /** The job site this is filed under; null on records saved before sites existed. */
  site_id: number | null;
  work_description: string;
  location: string;
  scheduled_start_date: DateString;
  attendees: string;
  prepared_by: string;
  prepared_date: DateString;
  reviewed_by: string;
  reviewed_date: DateString;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/** One hazard row: Sequence of Work / Potential Hazards / Method for Eliminating or Controlling and Person Responsible. */
export interface JhaStep {
  id: number;
  jha_id: number;
  seq: number;
  sequence_of_work: string;
  potential_hazards: string;
  control_method: string;
}

/** A JHA with its step rows, in seq order. */
export interface JhaFull {
  jha: Jha;
  steps: JhaStep[];
}

/** List-row shape: enough to identify a JHA without loading its steps. */
export interface JhaSummary {
  id: number;
  jobsite: string;
  /** The job site this is filed under; null on records saved before sites existed. */
  site_id: number | null;
  work_description: string;
  location: string;
  scheduled_start_date: DateString;
  step_count: number;
  updated_at: Timestamp;
}

/**
 * Concrete Field Testing: one log per record, a row per cylinder set.
 * Separate from the field QC app's ChuteTest — see the entity comment on
 * ChuteTest and the design spec for why no data flows between them.
 */
export interface FieldTestLog {
  id: number;
  title: string;
  notes: string;
  /** The job site this is filed under; null on records saved before sites existed. */
  site_id: number | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/**
 * One cylinder-set row, printed column order: Date Cast, Truck No., No.
 * Cast, Pile Number, Location, Mix, Slump, Temperature, De Moulded,
 * Specimen, 7 Day, 14 Day, 28 Day.
 *
 * `slump_mm`, `temperature_c` and the three break results are strings, not
 * numbers: the paper carries values like '150*' and 'Not recorded', which a
 * numeric field would refuse or silently lose.
 */
export interface FieldTestRow {
  id: number;
  log_id: number;
  seq: number;
  date_cast: DateString;
  truck_no: string;
  no_cast: string;
  pile_number: string;
  location: string;
  mix: string;
  slump_mm: string;
  temperature_c: string;
  de_moulded: string;
  specimen: string;
  break_7_day: string;
  break_14_day: string;
  break_28_day: string;
}

/** A field test log with its rows, in seq order. */
export interface FieldTestLogFull {
  field_test_log: FieldTestLog;
  rows: FieldTestRow[];
}

/** List-row shape: enough to identify a log without loading its rows. */
export interface FieldTestLogSummary {
  id: number;
  title: string;
  row_count: number;
  /** The job site this is filed under; null on records saved before sites existed. */
  site_id: number | null;
  updated_at: Timestamp;
}

/** Equipment Safety Inspection & Repair Report header. */
export interface EquipmentInspection {
  id: number;
  company: string;
  location: string;
  job_no: string;
  /** The job site this is filed under; null on records saved before sites existed. */
  site_id: number | null;
  equipment_no: string;
  hour_meter: string;
  inspection_date: DateString;
  shift: string;
  job_name: string;
  equipment_type: string;
  mileage: string;
  defects_explanation: string;
  repairs_needed: string;
  repairs_not_needed: string;
  operator_signature: string;
  operator_date: DateString;
  repairs_completed_by: string;
  repairs_completed_date: DateString;
  mechanic_signature: string;
  mechanic_date: DateString;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/**
 * One checklist row. `status` is the one constrained enum in this feature —
 * everything else here is a free-text record of what was observed.
 */
export interface EquipmentInspectionItem {
  id: number;
  inspection_id: number;
  seq: number;
  group_name: EquipmentChecklistGroup;
  label: string;
  status: InspectionStatus;
}

/** An equipment inspection with its checklist rows, in seq order. */
export interface EquipmentInspectionFull {
  equipment_inspection: EquipmentInspection;
  items: EquipmentInspectionItem[];
}

/** List-row shape: enough to identify an inspection without loading its items. */
export interface EquipmentInspectionSummary {
  id: number;
  /** The job this is filed under. The list groups by it. */
  job_name: string;
  /** The job site this is filed under; null on records saved before sites existed. */
  site_id: number | null;
  /*
   * Company, location and job number ride along on the list so a new
   * inspection started inside a job can fill them in from the job's most
   * recent record without a second request. They are job-level: they stay the
   * same across every machine on the job. Equipment number, hour meter and
   * mileage deliberately do NOT travel this way — see EquipmentForm.
   */
  company: string;
  location: string;
  job_no: string;
  equipment_no: string;
  inspection_date: DateString;
  /** Items whose status is 'rr'. Shown so the reader can see it at a glance — not a verdict. */
  rr_count: number;
  updated_at: Timestamp;
}

/** Daily Report header. */
export interface DailyReport {
  id: number;
  project: string;
  /** The job site this is filed under; null on records saved before sites existed. */
  site_id: number | null;
  weather: string;
  report_date: DateString;
  ticket_no: string;
  notes: string;
  prepared_by: string;
  reviewed_by: string;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/** One personnel time row. A new report seeds Khole, Dennis, Timour, Kenny. */
export interface DailyReportPerson {
  id: number;
  report_id: number;
  seq: number;
  name: string;
  time_in: TimeString;
  time_out: TimeString;
}

/** One delivery row. A new report seeds Sand, Gravel, Cement plus one Admixture row. */
export interface DailyReportDelivery {
  id: number;
  report_id: number;
  seq: number;
  material: string;
  amount: string;
}

/**
 * One piling activity row. `amount_supplied` and `water_used` are strings,
 * not numbers, for the same reason `slump_mm` and the break results are: the
 * paper carries values like '150*' and 'Not recorded'.
 *
 * `water_used` is LABELLED "Mix design" on the form and on the printed sheet.
 * The column kept its name: renaming it means a migration over live records to
 * buy nothing a comment cannot. The label is the contract with the user; the
 * column name is what the paper sheet used to ask for.
 */
export interface DailyReportActivity {
  id: number;
  report_id: number;
  seq: number;
  piling_number: string;
  unit_number: string;
  start_time: TimeString;
  stop_time: TimeString;
  amount_supplied: string;
  admixtures_used: string;
  water_used: string;
  /**
   * The mix design this pour used, for pricing. Set by the pick-list on the
   * form; `water_used` still carries the code as text for the printed sheet.
   * NULL on rows saved before the pick-list existed.
   */
  mix_design_id: number | null;
}

/** A daily report with its three independent child lists, each in seq order. */
export interface DailyReportFull {
  daily_report: DailyReport;
  personnel: DailyReportPerson[];
  deliveries: DailyReportDelivery[];
  activities: DailyReportActivity[];
}

/** List-row shape: enough to identify a report without loading its rows. */
export interface DailyReportSummary {
  id: number;
  project: string;
  /** The job site this is filed under; null on records saved before sites existed. */
  site_id: number | null;
  report_date: DateString;
  ticket_no: string;
  updated_at: Timestamp;
}

/* ==========================================================================
 * Entities — job sites
 *
 * One record per site that every quality form links to and the P&L groups
 * by. The typed job columns on the forms stay (spec option A): picking a
 * site writes its name into them, so printed sheets do not change and an old
 * record that matches no site still reads correctly.
 * docs/superpowers/specs/2026-08-26-job-sites-design.md
 * ======================================================================== */

export type SiteStatus = 'active' | 'finished';

export interface JobSite {
  id: number;
  name: string;
  street: string;
  city: string;
  province: string;
  postal_code: string;
  /** Finished sites leave the pick-lists and the active list; nothing else changes. */
  status: SiteStatus;
  start_date: DateString;
  end_date: DateString;
  notes: string;
  /**
   * Does this job bill for the concrete it pours? Default 1, so every job
   * that existed before this column behaves exactly as it did. 0 means the
   * client supplies the material: the site's pours are still recorded for
   * quality but earn nothing, and are NOT counted as unpriced — they are
   * deliberately unbilled, not missing a price.
   */
  bills_concrete: SqlBool;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/** A site as the lists show it: counts of its forms and when it was last touched. */
export interface SiteSummary extends JobSite {
  jha_count: number;
  field_test_count: number;
  equipment_count: number;
  daily_report_count: number;
  contract_report_count: number;
  last_activity: Timestamp | null;
}

/** One form on a site page: enough to list it and open it. */
export interface SiteRecord {
  id: number;
  label: string;
  date: DateString;
  updated_at: Timestamp;
}

export interface SiteResponse {
  site: JobSite;
  jha: SiteRecord[];
  field_tests: SiteRecord[];
  equipment: SiteRecord[];
  daily_reports: SiteRecord[];
  contract_reports: SiteRecord[];
}

export interface SiteListResponse {
  sites: SiteSummary[];
}

/* ==========================================================================
 * Quality — cement inventory (per job site)
 * ======================================================================== */

/**
 * A stock count: what was actually in the silo at the END of `count_date`.
 * The only thing a person types on the inventory screen; everything else is
 * read off sheets already filed. The running balance restarts from the latest
 * count: sheets dated after it are applied, sheets on or before it are taken
 * as already reflected in what was counted. Removed rows archive, never
 * delete, like every other recorded figure in the app.
 */
export interface CementStockCount {
  id: number;
  site_id: number;
  count_date: DateString;
  tonnes: number;
  note: string;
  archived: 0 | 1;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export interface SaveCementCountRequest {
  site_id: number;
  count_date: DateString;
  tonnes: number;
  note?: string;
}

export interface ArchiveCementCountRequest {
  id: number;
  archived: boolean;
}

/** Cement per cubic metre for one mix design, as its materials sheet says. */
export interface CementMixRate {
  mix_design_id: number;
  mix_code: string;
  /** kg of cement per m³ of concrete, or null when the sheet has no readable cement row. */
  kg_per_m3: number | null;
}

/** One delivery line on a Daily Report whose material is cement. */
export interface CementDelivery {
  report_id: number;
  report_date: DateString;
  ticket_no: string;
  material: string;
  amount_text: string;
  /** Tonnes delivered, or null with `reason` when the amount does not read. */
  tonnes: number | null;
  reason: string | null;
}

/** One pour, from a Daily or Contract Report, and the cement it consumed. */
export interface CementPour {
  source: 'daily' | 'contract';
  report_id: number;
  report_date: DateString;
  piling_number: string;
  unit_number: string;
  amount_text: string;
  m3: number | null;
  mix_design_id: number | null;
  mix_code: string;
  kg_per_m3: number | null;
  /** Tonnes of cement used, or null with `reason` when it cannot be worked out. */
  tonnes: number | null;
  reason: string | null;
}

export type CementLedgerKind = 'count' | 'delivery' | 'pour';

/** One line of the running ledger, oldest to newest. */
export interface CementLedgerLine {
  date: DateString;
  kind: CementLedgerKind;
  /** What the line is: the ticket, the piling, or the count's note. */
  label: string;
  detail: string;
  /** Tonnes in (a delivery); null on other kinds. */
  in_t: number | null;
  /** Tonnes out (a pour); null on other kinds. */
  out_t: number | null;
  /** Running balance after this line, in tonnes. */
  balance_t: number;
  /** True for lines dated before the first count: their balance assumes an empty silo. */
  before_first_count: boolean;
  source: 'daily' | 'contract' | 'count';
  record_id: number;
}

export interface CementLedger {
  /** Tonnes on hand after the newest line, or null when nothing has been recorded. */
  on_hand_t: number | null;
  /** The latest count the balance is anchored to, or null when there is none. */
  anchored_to: { count_date: DateString; tonnes: number } | null;
  /** Deliveries in and pours out since the anchor (or since the first sheet). */
  since_anchor: { in_t: number; out_t: number };
  lines: CementLedgerLine[];
  /** Deliveries and pours that could not be counted, with the reason each was left out. */
  uncounted: { deliveries: CementDelivery[]; pours: CementPour[] };
}

export interface CementInventoryResponse {
  site: JobSite;
  ledger: CementLedger;
  counts: CementStockCount[];
  mixes: CementMixRate[];
}

export interface SaveSiteRequest {
  name?: string;
  street?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  status?: SiteStatus;
  start_date?: DateString;
  end_date?: DateString;
  notes?: string;
  bills_concrete?: boolean;
}

/* ==========================================================================
 * Session
 * ======================================================================== */

/**
 * The signed HttpOnly cookie's decoded payload, and the shape returned by
 * GET /api/session. camelCase by spec mandate.
 */
export interface Session {
  projectId: number;
  projectCode: string;
  role: Role;
  /** Who is on shift. null until the shift picker has been used. */
  shiftName: string | null;
  /** Which area an office session belongs to. null for crew and client. */
  area: AccountArea | null;
}

/** Extra session context the frontend needs but the cookie need not carry. */
export interface SessionResponse {
  session: Session | null;
  project: PublicProject | null;
  /** true when the session came from a share link rather than a password. */
  via_share_token: boolean;
}

/* ==========================================================================
 * Computed values and acceptance rules
 * ======================================================================== */

/** CSA A23.1 tolerance for the water check and chute tests: +/-1%. */
export const TOLERANCE_FRACTION = 0.01;

/**
 * Decimal places the calibration check's actual wt/count is rounded to.
 *
 * The workbook rounds to 3 (`F6` = `ROUND(F7/F5,3)`). We deliberately keep 4:
 * at 3 dp a 0.076 reading moves in ~1.32% steps, which is coarser than the
 * tolerance being applied, so nearly every variance landed on the same handful
 * of values. At 4 dp a step is ~0.13% and the percentage actually discriminates
 * between readings. Changed 2026-08-04 at Geoff's request for exactly that.
 *
 * Consequence, recorded so it is not lost: this figure no longer reproduces the
 * workbook's displayed wt/count cell (0.0734 here vs the sheet's 0.073), and
 * Check 1 reads -3.42% rather than -3.95%. The underlying measurement is
 * unchanged; only the rounding applied before comparison is.
 */
export const CHECK_WT_PER_COUNT_DECIMALS = 4;

/**
 * Tolerance for the calibration check: +/-2%.
 *
 * This is a SITE tolerance, NOT CSA A23.1, and must never be labelled as such
 * anywhere a person can read it. CSA A23.1 is +/-1% (see TOLERANCE_FRACTION).
 *
 * Its original justification NO LONGER APPLIES, and that matters if anyone is
 * deciding whether to keep it. It was chosen on 2026-07-29 when wt/count was
 * rounded to 3 dp: a 0.001 step is ~1.32% of a 0.076 reading, so a +/-1% band
 * could only ever pass on an exact match, the resolution being coarser than the
 * tolerance. Moving to 4 dp on 2026-08-04 cut a step to ~0.13%, so rounding no
 * longer forces a wide band — CSA's +/-1% would now be a workable test.
 *
 * +/-2% therefore stands as a deliberate SITE allowance, not a rounding
 * artefact. Keeping it is a judgement about how tightly this mixer should be
 * held, and it is the looser choice: at 4 dp the measurement supports +/-1%.
 * Raised with Geoff 2026-08-04; unchanged pending that decision. See
 * docs/superpowers/specs/2026-07-29-calibration-checks-design.md.
 */
export const CHECK_TOLERANCE_FRACTION = 0.02;

/** Number of calibration checks per pour. */
export const CALIBRATION_CHECK_COUNT = 2;

/** Spread acceptance range, INCHES. */
export const SPREAD_MIN_IN = 16;
export const SPREAD_MAX_IN = 19;

/** Flow acceptance range, SECONDS. */
export const FLOW_MIN_S = 40;
export const FLOW_MAX_S = 70;

export type Verdict = 'pass' | 'fail';

/** Result of a +/-1% tolerance check. */
export interface ToleranceCheck {
  /** The computed value being judged (e.g. computer kg, or litres/kg). */
  value: number;
  /** The reference the value is compared against. */
  reference: number;
  /** Signed relative deviation, (value - reference) / reference. */
  deviation: number;
  /** Absolute deviation as a percentage, for display. */
  deviation_percent: number;
  verdict: Verdict;
}

export type SpreadVerdict = 'in_range' | 'add_water' | 'reduce_water';
export type FlowVerdict = 'in_range' | 'too_much_water' | 'not_enough_water';

/** Human labels for the range verdicts. */
export const SPREAD_VERDICT_LABELS: Record<SpreadVerdict, string> = {
  in_range: 'In range (16-19 in)',
  add_water: 'Below 16 in - add water',
  reduce_water: 'Above 19 in - reduce water dosage rate'
};

export const FLOW_VERDICT_LABELS: Record<FlowVerdict, string> = {
  in_range: 'In range (40-70 s)',
  too_much_water: 'Below 40 s - too much water',
  not_enough_water: 'Above 70 s - not enough water'
};

/**
 * Everything computed for a single calibration trial row.
 *
 * A trial carries no verdict — see `CalibrationTrial`.
 */
export interface CalibrationTrialComputed {
  trial_no: number;
  /** reading_kg / counts, null if either input is missing or counts is 0 */
  wt_per_count: number | null;
}

/** Everything computed for a single calibration check row. */
export interface CalibrationCheckComputed {
  check_no: number;
  /**
   * ROUND(actual_kg / actual_counts, 3). Null if either input is missing or
   * actual_counts is 0.
   *
   * This rounded figure is the JUDGED value, not a display value — the one
   * place in this codebase where that is true. See `actualWtPerCount`.
   */
  actual_wt_per_count: number | null;
  /**
   * (actual_wt_per_count - check_wt_per_count) / check_wt_per_count, judged at
   * the +/-2% SITE tolerance (never CSA A23.1). Null until both the actual
   * readings and the entered check wt/count exist — an unanswered check is not
   * a failed check.
   */
  variance: ToleranceCheck | null;
}

/** Everything computed for the calibration worksheet as a whole. */
export interface CalibrationComputed {
  trials: CalibrationTrialComputed[];
  /** Mean of the non-null per-trial wt/count values, null if none. */
  wt_per_count_mean: number | null;
  /** The two verification checks, in check_no order. */
  checks: CalibrationCheckComputed[];
  /** trial_water_kg / trial_water_litres judged at +/-1%. */
  water_check: ToleranceCheck | null;
}

/** Everything computed for a chute test row. */
export interface ChuteTestComputed {
  chute_test_id: number;
  /** water_vol_l / grout_wt_kg */
  wc_ratio: number | null;
  spread_verdict: SpreadVerdict | null;
  flow_verdict: FlowVerdict | null;
  /** Always null: no acceptance range has been supplied for mud balance. */
  mud_balance_verdict: null;
}

/* ==========================================================================
 * Seed data — Appendix A and Appendix B
 * The API seeds these rows verbatim when a project / pour is created.
 * ======================================================================== */

export interface SeedItem {
  item_no: number;
  title: string;
  point_type: PointType;
}

/** ITP items 1-5, seeded into job_setup_item when a project is created. */
export const JOB_SETUP_ITEMS: readonly SeedItem[] = [
  { item_no: 1, title: 'Pre-job quality review', point_type: 'H' },
  { item_no: 2, title: 'Personnel readiness / training confirmation', point_type: 'H/R' },
  { item_no: 3, title: 'Material documentation', point_type: 'H/R' },
  { item_no: 4, title: 'Manufacturer pump/application confirmation', point_type: 'H/R' },
  { item_no: 5, title: 'Water source documentation', point_type: 'H/R' }
] as const;

/** ITP items 6-31, seeded into itp_item when a pour is created. */
export const POUR_ITP_ITEMS: readonly SeedItem[] = [
  { item_no: 6, title: 'Material supply and loading', point_type: 'S' },
  { item_no: 7, title: 'Grout lot/batch tracking', point_type: 'R' },
  { item_no: 8, title: 'Equipment cleanout / dedication', point_type: 'W' },
  { item_no: 9, title: 'Equipment inspection', point_type: 'S' },
  { item_no: 10, title: 'Pump/line setup confirmed', point_type: 'H/W' },
  { item_no: 11, title: 'Final acceptance point confirmed', point_type: 'H/R' },
  { item_no: 12, title: 'Pre-pour dry grout feed calibration', point_type: 'H' },
  { item_no: 13, title: 'Pre-pour water flow meter verification', point_type: 'H' },
  { item_no: 14, title: 'Approved production rate confirmed', point_type: 'H/R' },
  { item_no: 15, title: 'Dry feed and water input stability', point_type: 'H/S' },
  { item_no: 16, title: 'Trial/start-up discharge', point_type: 'H' },
  { item_no: 17, title: 'Initial chute test', point_type: 'H/W' },
  { item_no: 18, title: 'Interval chute testing', point_type: 'W/S' },
  { item_no: 19, title: 'Additional testing after changes', point_type: 'H/W' },
  { item_no: 20, title: 'Temperature monitoring', point_type: 'S' },
  { item_no: 21, title: 'Material feed and water control monitoring', point_type: 'S' },
  { item_no: 22, title: 'Continuous flow / pump coordination', point_type: 'S' },
  { item_no: 23, title: 'Water dosage monitoring', point_type: 'S/R' },
  { item_no: 24, title: 'Volume tracking / uniform yield record', point_type: 'R' },
  { item_no: 25, title: 'Stop-hold action plan', point_type: 'H' },
  { item_no: 26, title: 'Controlled adjustment record', point_type: 'R' },
  { item_no: 27, title: 'Restart after hold/stop', point_type: 'H' },
  { item_no: 28, title: 'Production ticket / grout log', point_type: 'R' },
  { item_no: 29, title: 'Cleanout', point_type: 'S' },
  { item_no: 30, title: 'Document submission timing confirmed', point_type: 'R' },
  { item_no: 31, title: 'Final documentation review', point_type: 'R' }
] as const;

/** Appendix B — the 14 end-of-pour items, seeded when a pour is created. */
export const END_OF_POUR_ITEMS: readonly { item_no: number; title: string }[] = [
  { item_no: 1, title: 'Pre-job quality review completed' },
  { item_no: 2, title: 'Personnel readiness/training confirmed' },
  { item_no: 3, title: 'Material documentation received/accepted' },
  { item_no: 4, title: 'Potable water documentation received/accepted' },
  { item_no: 5, title: 'Material lot/batch tracking completed' },
  { item_no: 6, title: 'Pump/line setup and acceptance point confirmed' },
  { item_no: 7, title: 'Pre-pour calibration completed and documented' },
  { item_no: 8, title: 'Initial chute/acceptance-point test completed by Kiewit QC/tester' },
  { item_no: 9, title: 'Interval tests completed every 3 m3 or as directed' },
  { item_no: 10, title: 'All water/feed/production adjustments documented' },
  { item_no: 11, title: 'All hold/stop events documented and closed' },
  { item_no: 12, title: 'Volume tracking / grout log completed' },
  { item_no: 13, title: 'SCI equipment cleanout completed before grout set' },
  { item_no: 14, title: 'Records provided to Kiewit/QC for review' }
] as const;

/** Number of calibration trial rows seeded per pour. */
export const CALIBRATION_TRIAL_COUNT = 3;

/* ==========================================================================
 * API — errors
 * ======================================================================== */

export type ApiErrorCode =
  | 'bad_request'
  | 'validation_failed'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'rate_limited'
  | 'hold_point_open'
  | 'internal_error';

/** Every non-2xx JSON response has this shape. */
export interface ApiErrorBody {
  error: {
    code: ApiErrorCode;
    message: string;
    /** field -> message, for validation failures */
    fields?: Record<string, string>;
    /** ITP items blocking a close, present only when code === 'hold_point_open' */
    blocking_items?: ItpItem[];
  };
}

/** Trivial acknowledgement body. */
export interface OkResponse {
  ok: true;
}

/* ==========================================================================
 * API — auth
 * ======================================================================== */

/**
 * POST /api/auth/login.
 *
 * `code` is either a project code (e.g. 'KiewitWF', yielding a crew or client
 * session) or a company account login (e.g. 'scimixdesign', yielding an
 * office session) — one endpoint, one identifier field. Case-insensitive on
 * the server either way.
 */
export interface LoginRequest {
  code: string;
  password: string;
  /** Optional: set the shift name in the same round trip. Ignored for an account login. */
  shift_name?: string | null;
}

/**
 * POST /api/auth/login, POST /api/auth/share-login, POST /api/session/shift.
 *
 * `project` is null for an office session — the mix design library is
 * company-wide, not project-scoped. That absence is the point.
 */
export interface LoginResponse {
  session: Session;
  project: PublicProject | null;
}

/** POST /api/auth/share-login — arrival via /share/<code>/<token> */
export interface ShareLoginRequest {
  code: string;
  token: string;
  shift_name?: string | null;
}

/** POST /api/session/shift */
export interface SetShiftRequest {
  /** null clears the shift name. */
  shift_name: string | null;
}

/* ==========================================================================
 * API — mix designs
 * ======================================================================== */

/** One materials row as sent by the client. `id` is not sent: rows are replaced. */
export interface MixDesignMaterialInput {
  name: string;
  amount: string;
  unit: MixMaterialUnit;
  density_kg_m3?: number | null;
  volume_m3?: number | null;
}

/**
 * POST /api/mix-designs and PATCH /api/mix-designs/:mixId — office only.
 *
 * The WHOLE document, header and rows together. There are no per-row routes:
 * one save is one consistent sheet, and the server replaces the material rows
 * transactionally. Omitting `materials` leaves the existing rows untouched.
 */
export interface SaveMixDesignRequest {
  project_name?: string;
  mix_code?: string;
  subject?: string;
  slump?: string;
  specified_strength?: string;
  wc_ratio?: number | null;
  wc_ratio_max?: number | null;
  yield_m3?: number;
  notes?: string;
  materials?: MixDesignMaterialInput[];
}

export interface MixDesignResponse {
  mix_design: MixDesign;
  materials: MixDesignMaterial[];
}

export interface MixDesignListResponse {
  mix_designs: MixDesignSummary[];
}

/* ==========================================================================
 * API — quality area
 *
 * One route group per form. Each save sends the whole document — header and
 * every child list together — and the server replaces the child rows in one
 * D1 batch, exactly as mix designs do: an absent child key means "leave
 * those rows alone", present-but-empty means "this record has none". No
 * per-row routes.
 * ======================================================================== */

/* --------------------------------------------------------------- JHA --- */

/** One step row as sent by the client. `id` is not sent: rows are replaced. */
export interface JhaStepInput {
  sequence_of_work: string;
  potential_hazards: string;
  control_method: string;
}

/**
 * POST /api/quality/jha and PATCH /api/quality/jha/:jhaId — office/quality only.
 *
 * The WHOLE document, header and steps together. Omitting `steps` leaves the
 * existing rows untouched. A new JHA starts with no steps — the paper's blank
 * rows are just space, not content.
 */
export interface SaveJhaRequest {
  jobsite?: string;
  site_id?: number | null;
  work_description?: string;
  location?: string;
  scheduled_start_date?: DateString;
  attendees?: string;
  prepared_by?: string;
  prepared_date?: DateString;
  reviewed_by?: string;
  reviewed_date?: DateString;
  steps?: JhaStepInput[];
}

export interface JhaResponse {
  jha: Jha;
  steps: JhaStep[];
}

export interface JhaListResponse {
  jha: JhaSummary[];
}

/* ------------------------------------------------------- field tests --- */

/** One log row as sent by the client. `id` is not sent: rows are replaced. */
export interface FieldTestRowInput {
  date_cast: DateString;
  truck_no: string;
  no_cast: string;
  pile_number: string;
  location: string;
  mix: string;
  slump_mm: string;
  temperature_c: string;
  de_moulded: string;
  specimen: string;
  break_7_day: string;
  break_14_day: string;
  break_28_day: string;
}

/**
 * POST /api/quality/field-tests and PATCH /api/quality/field-tests/:fieldTestId
 * — office/quality only.
 *
 * The WHOLE document, header and rows together. Omitting `rows` leaves the
 * existing rows untouched. A new log starts with no rows — the paper's blank
 * rows are just space, not content.
 */
export interface SaveFieldTestLogRequest {
  title?: string;
  notes?: string;
  site_id?: number | null;
  rows?: FieldTestRowInput[];
}

export interface FieldTestLogResponse {
  field_test_log: FieldTestLog;
  rows: FieldTestRow[];
}

export interface FieldTestLogListResponse {
  field_test_logs: FieldTestLogSummary[];
}

/* ---------------------------------------------------------- equipment --- */

/** One checklist row as sent by the client. `id` is not sent: rows are replaced. */
export interface EquipmentInspectionItemInput {
  group_name: EquipmentChecklistGroup;
  label: string;
  status: InspectionStatus;
}

/**
 * POST /api/quality/equipment and PATCH /api/quality/equipment/:equipmentId
 * — office/quality only.
 *
 * The WHOLE document, header and checklist items together. Omitting `items`
 * leaves the existing rows untouched. A new inspection with no `items`
 * supplied is seeded with the full printed checklist — see EQUIPMENT_CHECKLIST
 * in ./seed-data.ts.
 */
export interface SaveEquipmentInspectionRequest {
  company?: string;
  location?: string;
  job_no?: string;
  site_id?: number | null;
  equipment_no?: string;
  hour_meter?: string;
  inspection_date?: DateString;
  shift?: string;
  job_name?: string;
  equipment_type?: string;
  mileage?: string;
  defects_explanation?: string;
  repairs_needed?: string;
  repairs_not_needed?: string;
  operator_signature?: string;
  operator_date?: DateString;
  repairs_completed_by?: string;
  repairs_completed_date?: DateString;
  mechanic_signature?: string;
  mechanic_date?: DateString;
  items?: EquipmentInspectionItemInput[];
}

export interface EquipmentInspectionResponse {
  equipment_inspection: EquipmentInspection;
  items: EquipmentInspectionItem[];
}

export interface EquipmentInspectionListResponse {
  equipment_inspections: EquipmentInspectionSummary[];
}

/* -------------------------------------------------------- daily report --- */

/** One personnel row as sent by the client. `id` is not sent: rows are replaced. */
export interface DailyReportPersonInput {
  name: string;
  time_in: TimeString;
  time_out: TimeString;
}

/** One delivery row as sent by the client. `id` is not sent: rows are replaced. */
export interface DailyReportDeliveryInput {
  material: string;
  amount: string;
}

/** One activity row as sent by the client. `id` is not sent: rows are replaced. */
/**
 * A Daily Report extra: which activity and how many. Priced in the admin P&L
 * against `activity_rate`; the quality area only ever sees the names.
 */
export interface DailyReportOtherActivity {
  id: number;
  report_id: number;
  seq: number;
  activity: string;
  quantity: string;
}

export interface DailyReportOtherActivityInput {
  activity: string;
  quantity: string;
}

export interface DailyReportActivityInput {
  piling_number: string;
  unit_number: string;
  start_time: TimeString;
  stop_time: TimeString;
  amount_supplied: string;
  admixtures_used: string;
  water_used: string;
  mix_design_id?: number | null;
}

/**
 * POST /api/quality/daily-reports and PATCH /api/quality/daily-reports/:dailyReportId
 * — office/quality only.
 *
 * The WHOLE document, header and all three child lists together. Each of
 * `personnel`, `deliveries` and `activities` is independently three-state:
 * absent leaves those rows untouched, present-but-empty means none, present
 * replaces the rows wholesale. A new report with `deliveries` not supplied is
 * seeded with the documented defaults — see DAILY_REPORT_DEFAULT_DELIVERIES in
 * ./seed-data.ts. `personnel` and `activities` start EMPTY either way:
 * personnel used to seed four names, and every one the crew did not work with
 * saved as a name with no clock-in that the P&L could not price.
 */
export interface SaveDailyReportRequest {
  project?: string;
  site_id?: number | null;
  weather?: string;
  report_date?: DateString;
  ticket_no?: string;
  notes?: string;
  prepared_by?: string;
  reviewed_by?: string;
  personnel?: DailyReportPersonInput[];
  deliveries?: DailyReportDeliveryInput[];
  activities?: DailyReportActivityInput[];
  other_activities?: DailyReportOtherActivityInput[];
}

export interface DailyReportResponse {
  daily_report: DailyReport;
  personnel: DailyReportPerson[];
  deliveries: DailyReportDelivery[];
  activities: DailyReportActivity[];
  /** Always sent by the server; optional so shapes built by hand (tests, print previews) need not carry it. */
  other_activities?: DailyReportOtherActivity[];
}

export interface DailyReportListResponse {
  daily_reports: DailyReportSummary[];
}

/**
 * A picture attached to a Daily Report. Deliberately NOT the crew-side
 * `Photo`: that entity is project/pour scoped and travels the signed-grant
 * pipeline; this one belongs to the office/quality area and is uploaded in
 * one authenticated request. `r2_key` is derived from the content hash, so
 * the same picture attached twice collapses onto one row.
 */
export interface DailyReportPhoto {
  id: number;
  report_id: number;
  r2_key: string;
  caption: string;
  bytes: number | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export interface DailyReportPhotoResponse {
  photo: DailyReportPhoto;
}

export interface DailyReportPhotoListResponse {
  photos: DailyReportPhoto[];
}

/** The mix design library as the quality area sees it: a name to pick, no price. */
export interface MixDesignOption {
  id: number;
  mix_code: string;
  project_name: string;
}

export interface MixDesignOptionsResponse {
  mix_designs: MixDesignOption[];
}

/* ==========================================================================
 * Entities — the admin area (P&L)
 *
 * Three effective-dated price tables. A row is created or deleted, never
 * updated: the price in force on a date is the row with the greatest
 * `effective_from` on or before it. See
 * docs/superpowers/specs/2026-08-25-admin-pnl-design.md.
 * ======================================================================== */

export interface MaterialPrice {
  id: number;
  material: string;
  /**
   * null is the default price: it applies to any site without one of its
   * own, exactly as a site-less trucking rate does. Rows saved before prices
   * could be set per site are all null, so they keep pricing every site.
   */
  site_id: number | null;
  cost_per_tonne: number;
  effective_from: DateString;
  note: string;
  /**
   * Removed from the Rates list, history kept. Set on every row of the
   * material together. An archived material still prices any past day it
   * appears on — deleting its rows would unprice every one of them instead.
   */
  archived: SqlBool;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export interface WorkerRate {
  id: number;
  name: string;
  hourly_rate: number;
  effective_from: DateString;
  note: string;
  /**
   * Removed from the Rates list, history kept. Set on every row of the name
   * together. An archived worker is still priced on any day they appear —
   * deleting their rows would unprice every past day instead.
   */
  archived: SqlBool;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export interface MixDesignPrice {
  id: number;
  mix_design_id: number;
  sell_per_m3: number;
  effective_from: DateString;
  note: string;
  /**
   * Removed from the Rates list, history kept. Set on every price row of the
   * mix design together. An archived mix design still prices any past day it
   * appears on — deleting its rows would unprice every one of them instead.
   */
  archived: SqlBool;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/**
 * What SCI BILLS for an extra on a job day — Standby by the hour, Delivery by
 * the load, a Small load fee, or anything else the admin names. REVENUE, not
 * cost. Per site with a site-less default, effective-dated like every price.
 */
export interface ActivityRate {
  id: number;
  activity: string;
  /** What one of the crew's "how many" is: 'hour', 'load' or 'each'. */
  unit: ActivityUnit;
  /** null is the default rate, used by any site without one of its own. */
  site_id: number | null;
  rate: number;
  effective_from: DateString;
  note: string;
  archived: SqlBool;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export type ActivityUnit = 'hour' | 'load' | 'each';

export interface ActivityRateListResponse {
  activity_rates: ActivityRate[];
}

export interface CreateActivityRateRequest {
  activity: string;
  unit: ActivityUnit;
  site_id?: number | null;
  rate: number;
  effective_from: DateString;
  note?: string;
}

export interface TruckingRate {
  id: number;
  /** null is the default rate: it applies to any site without one of its own. */
  site_id: number | null;
  cost_per_tonne: number;
  /**
   * The materials this rate is charged on: folded names, comma-separated.
   * Empty is LEGACY ONLY — it is what rows entered before this column existed
   * mean, "every material on the report". Every new row names its materials,
   * so do not read empty as the default for anything being written now.
   */
  materials: string;
  effective_from: DateString;
  note: string;
  /**
   * Removed from the Rates list, history kept. Set on every row of the site
   * (or, for the default rate, every row with a null site_id) together. An
   * archived rate still prices any past day it appears on — deleting its
   * rows would unprice every one of them instead.
   */
  archived: SqlBool;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export type TaxKind = 'gst' | 'pst';

/**
 * Every price in this app is entered ex-tax; these are what tax is added at.
 * GST is recoverable, so it is neither revenue nor cost — it is collected on
 * concrete and paid on materials and trucking, and the difference is remitted.
 * PST is not recoverable, so it is a real cost of the materials it is charged
 * on. Neither applies to labour.
 */
export interface TaxRate {
  id: number;
  kind: TaxKind;
  /** 5 means 5%. */
  percent: number;
  effective_from: DateString;
  note: string;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/* ==========================================================================
 * API — admin area
 * ======================================================================== */

export interface CreateMaterialPriceRequest {
  material: string;
  /** Absent or null for the default price, used by any site without its own. */
  site_id?: number | null;
  cost_per_tonne: number;
  effective_from: DateString;
  note?: string;
}

export interface CreateWorkerRateRequest {
  name: string;
  hourly_rate: number;
  effective_from: DateString;
  note?: string;
}

export interface CreateMixPriceRequest {
  mix_design_id: number;
  sell_per_m3: number;
  effective_from: DateString;
  note?: string;
}

/** Omit `site_id`, or send null, for the default rate. */
export interface CreateTruckingRateRequest {
  site_id?: number | null;
  cost_per_tonne: number;
  /** Which of TRUCKED_MATERIALS the rate is charged on. Any other name is refused. */
  materials?: string[];
  effective_from: DateString;
  note?: string;
}

export interface CreateTaxRateRequest {
  kind: TaxKind;
  percent: number;
  effective_from: DateString;
  note?: string;
}

/** Which of an equipment rate's figures a contract row bills on. */
export type RateUnit = 'day' | 'week' | 'month';

/**
 * What SCI bills for a named piece of equipment. Not a cost. `charge_per_day`
 * is always set; the weekly and monthly figures are optional (null = not
 * offered at that unit) — a row that picks an unpriced unit is flagged, never
 * billed at 0.
 */
export interface CreateEquipmentRateRequest {
  equipment: string;
  charge_per_day: number;
  charge_per_week?: number | null;
  charge_per_month?: number | null;
  effective_from: DateString;
  note?: string;
}

/** What SCI bills per day for a named operator. Not their wage. */
export interface CreateOperatorRateRequest {
  name: string;
  charge_per_day: number;
  effective_from: DateString;
  note?: string;
}

export interface CreateCancellationFeeRequest {
  amount: number;
  effective_from: DateString;
  note?: string;
}

export interface MaterialPriceListResponse {
  material_prices: MaterialPrice[];
}

export interface WorkerRateListResponse {
  worker_rates: WorkerRate[];
}

export interface EquipmentRateListResponse {
  equipment_rates: EquipmentRate[];
}

export interface OperatorRateListResponse {
  operator_rates: OperatorRate[];
}

export interface CancellationFeeListResponse {
  cancellation_fees: CancellationFee[];
}

/**
 * What SCI charges for a named piece of equipment on site. NOT a cost.
 * `charge_per_day` is always present; `charge_per_week`/`charge_per_month` are
 * null when the machine is not offered at that unit — the day case is the only
 * one guaranteed to price, matching the schema's nullable columns.
 */
export interface EquipmentRate {
  id: number;
  equipment: string;
  charge_per_day: number;
  charge_per_week: number | null;
  charge_per_month: number | null;
  effective_from: DateString;
  note: string;
  archived: SqlBool;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/**
 * $/day charged out for a named operator. NOT what they are paid — that is
 * WorkerRate.hourly_rate, and confusing the two puts revenue in the cost
 * column where it will still look plausible.
 */
export interface OperatorRate {
  id: number;
  name: string;
  charge_per_day: number;
  effective_from: DateString;
  note: string;
  archived: SqlBool;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/** A flat fee billed when a contract day is cancelled. */
export interface CancellationFee {
  id: number;
  amount: number;
  effective_from: DateString;
  note: string;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export interface ContractReportEquipment {
  id: number;
  report_id: number;
  seq: number;
  equipment: string;
  /** Retired — the day count is derived from `start_date`/`end_date` instead. Left readable so old callers still compile; calc/contract.ts no longer reads it. */
  days: number;
  /** Blank means "use the card's own range" — see calc/contract.ts. Optional because rows written before this field existed, and some in-repo fixtures, do not carry it. */
  start_date?: DateString;
  end_date?: DateString;
  /**
   * Which of the machine's rates this row bills on. Optional here because rows
   * written before this field existed do not carry it; calc/contract.ts and the
   * write path both read an absent value as `'day'`, the pre-existing behaviour.
   */
  rate_unit?: RateUnit;
}

export interface ContractReportOperator {
  id: number;
  report_id: number;
  seq: number;
  name: string;
  days: number;
  time_in: string;
  time_out: string;
}

export interface ContractReport {
  id: number;
  site_id: number | null;
  report_date: DateString;
  ticket_no: string;
  prepared_by: string;
  reviewed_by: string;
  notes: string;
  cancelled: 0 | 1;
  cancellation_note: string;
  /** The card's own last day; blank means one day (`report_date` alone). Optional because fixtures and callers written before this field existed do not carry it. */
  end_date?: DateString;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/**
 * What SCI hired IN for a contract job — a rented machine or a
 * subcontractor's crew, on site over its own date range. Same shape either
 * way: a named third party, billing per day. `cost_per_day` is what SCI
 * PAYS — a cost, never the `equipment_rate.charge_per_day` SCI bills — and
 * is admin-only: never sent to a quality session or printed on the card.
 */
export interface ContractReportHire {
  id: number;
  report_id: number;
  seq: number;
  name: string;
  kind: 'equipment' | 'subcontractor';
  start_date: DateString;
  end_date: DateString;
  cost_per_day: number;
}

/**
 * A hire as a non-admin session reads it — `cost_per_day` stripped, the same
 * way `PublicProject` strips the password hashes. `ContractReportResponse`'s
 * `hires` is typed to admit either shape because which one comes back
 * depends on the session's area, decided server-side in
 * quality-contract-reports.ts, never left to the client to enforce.
 */
export type PublicContractReportHire = Omit<ContractReportHire, 'cost_per_day'>;

/**
 * A pour on a contract day. Same shape as DailyReportActivity — m³ and mix
 * are captured the same way wherever they happen — but its own table, since
 * it hangs off a ContractReport rather than a DailyReport.
 */
export interface ContractReportActivity {
  id: number;
  report_id: number;
  seq: number;
  piling_number: string;
  unit_number: string;
  start_time: string;
  stop_time: string;
  amount_supplied: string;
  admixtures_used: string;
  water_used: string;
  mix_design_id: number | null;
}

export interface ContractReportResponse {
  contract_report: ContractReport;
  equipment: ContractReportEquipment[];
  operators: ContractReportOperator[];
  activities: ContractReportActivity[];
  /**
   * What was hired in. Each row is a `ContractReportHire` for an admin
   * session and a `PublicContractReportHire` (no `cost_per_day`) for a
   * quality session — see that type's comment.
   */
  hires: (ContractReportHire | PublicContractReportHire)[];
}

export interface ContractReportListResponse {
  contract_reports: ContractReport[];
}

/**
 * POST /api/quality/contract-reports and PATCH /api/quality/contract-reports/:contractReportId
 * — office/quality only.
 *
 * The WHOLE document, header and all four child lists together. Each of
 * `equipment`, `operators`, `activities` and `hires` is independently
 * three-state: absent leaves those rows untouched, present-but-empty means
 * none, present replaces the rows wholesale. Every list starts EMPTY on a
 * create with no key supplied — a contract day has no default equipment or
 * crew the way a Daily Report's deliveries do.
 *
 * `equipment` and `hires` are id-stable on a "present" save (Task 4 review,
 * C1/I1/I2/I5): a row whose `id` matches one already stored on this report
 * is UPDATED in place, not deleted and reissued a new id, and a column the
 * incoming row doesn't mention at all is left untouched rather than blanked.
 * A row carrying NO `id` is matched on what it is instead — its folded name,
 * and (for a hire) its kind and dates, or (for equipment) whichever dates it
 * mentioned — because `id` is optional here and a client that builds its
 * payload from form state sends none. Only a row matching neither is new.
 * An `id` that names no row on THIS report matches nothing and does not fall
 * back. This is
 * NOT wholesale replace, unlike `operators`/`activities`, because each
 * carries server-owned data a session cannot always read back and resend:
 * `hires[].cost_per_day` for a non-admin session (stripped from every read,
 * see `ContractReportHire`'s standing invariant — sending it anyway, or
 * omitting it, never errors, it simply has no effect for that session), and
 * `equipment[].start_date`/`end_date` for any client using the legacy
 * `{equipment, days}` shape that predates ranges — omitting either key
 * preserves the stored value; sending one, even as `''`, is a real,
 * different value ("use the card's own range").
 */
export interface SaveContractReportRequest {
  site_id?: number | null;
  report_date?: DateString;
  /** Blank means the card is one day (`report_date` alone). Rejected with a 422 if malformed or before `report_date`. */
  end_date?: DateString;
  ticket_no?: string;
  prepared_by?: string;
  reviewed_by?: string;
  notes?: string;
  cancelled?: boolean;
  cancellation_note?: string;
  /**
   * `start_date`/`end_date` blank means "use the card's own range" — see
   * calc/contract.ts. `days` is still accepted but no longer read. `id`
   * makes the row id-stable (see this interface's own header).
   */
  equipment?: { id?: number; equipment: string; days: number; start_date?: DateString; end_date?: DateString; rate_unit?: RateUnit }[];
  operators?: { name: string; days: number; time_in?: TimeString; time_out?: TimeString }[];
  activities?: DailyReportActivityInput[];
  hires?: {
    /**
     * Names WHICH stored row this is (see this interface's own header). Not
     * required — a row without one is matched on its folded name, kind and
     * dates instead — but a non-admin save that would drop a stored row
     * carrying a non-zero `cost_per_day` without naming it here is refused
     * with a 422, because an edited row with no id cannot be told apart
     * from a new one.
     */
    id?: number;
    name: string;
    kind?: 'equipment' | 'subcontractor';
    start_date?: DateString;
    end_date?: DateString;
    /**
     * Admin only. `undefined`, `null`, or a blank/whitespace string all mean
     * "not supplied" — the stored value is preserved, never zeroed. An
     * explicit `0` really does store a 0, but note what a stored 0 MEANS:
     * the column is `NOT NULL DEFAULT 0`, so it cannot tell "free" from
     * "not yet priced", and every money surface reads it as UNPRICED — a
     * reason and no figure, never a settled-looking $0 (see
     * `CONTRACT_UNPRICED.noHireCost`). There is no way to record a hire
     * that genuinely costs nothing, and no real third party charges
     * nothing. Anything else that is not a valid, non-negative number is
     * rejected with a 422.
     *
     * `string` is a real, accepted shape here (Task 5 review, C1), not just
     * something `readHireCost` happens to tolerate: a client that lets the
     * user TYPE a figure should send the raw trimmed text and let this
     * route's own validator judge it, rather than pre-parsing client-side —
     * `Number(x) || undefined`-style coercion silently turns an unparsable
     * typo ("45O", "1,200") into "not supplied", which preserves the OLD
     * stored figure and reports success, hiding a real data-entry mistake
     * from both the admin and the server's own 422.
     */
    cost_per_day?: number | string | null;
  }[];
}

/**
 * One name seen on a Daily Report that carries no rate.
 *
 * `report_count` and `last_seen` are the evidence for chasing it: a name on
 * six reports last week is a payroll gap, one on a single report last spring
 * is probably a visitor.
 */
export interface RateGap {
  /** The first spelling seen, trimmed. Shown to a human, so not folded. */
  name: string;
  report_count: number;
  /** The most recent report date this name appeared on, yyyy-mm-dd. */
  last_seen: string;
}

/**
 * What still needs a dollar amount, on both sides of the Rates screen.
 *
 * One response rather than two, because the Rates screen and the dashboard
 * chip both ask this question and must never answer it differently.
 */
export interface RateGapsResponse {
  workers: RateGap[];
  materials: RateGap[];
  /**
   * Names deliberately hidden from the two lists above, so the screen can
   * offer them back. Names only — a dismissed name has nothing to price.
   */
  dismissed: {
    workers: string[];
    materials: string[];
  };
}

/** Which list a dismissed name belongs to. The two are separate namespaces. */
export type DismissKind = 'worker' | 'material';

/**
 * Stop (or resume) asking for a rate for one name.
 *
 * Hides it from the Rates gap list and the dashboard count. Touches no Daily
 * Report: the rows stay, and the P&L still reports those lines as unpriced,
 * because they still are.
 */
export interface DismissNameRequest {
  kind: DismissKind;
  name: string;
  dismissed: boolean;
}

/** Remove a worker from the list (or restore them). Matches the name case-insensitively. */
export interface ArchiveWorkerRequest {
  name: string;
  archived: boolean;
}

/** Remove a material from the list (or restore it). Matches the name case-insensitively. */
export interface ArchiveMaterialRequest {
  material: string;
  /** Which price list: absent or null is the default one, a number one site's own. */
  site_id?: number | null;
  archived: boolean;
}

/** Remove an activity rate from the list (or restore it), for one site or the default (null). */
export interface ArchiveActivityRequest {
  activity: string;
  site_id?: number | null;
  archived: boolean;
}

/** Remove a mix design's sell price from the list (or restore it). Keyed by mix_design_id — a number, so no case fold. */
export interface ArchiveMixRequest {
  mix_design_id: number;
  archived: boolean;
}

/**
 * Remove a trucking rate from the list (or restore it). Keyed by site_id;
 * absent or null targets the default rate, the one with a null site_id.
 */
export interface ArchiveTruckingRequest {
  site_id?: number | null;
  archived: boolean;
}

/** Remove a piece of equipment from the list (or restore it). Matches the name case-insensitively. */
export interface ArchiveEquipmentRequest {
  equipment: string;
  archived: boolean;
}

/** Remove an operator from the list (or restore them). Matches the name case-insensitively. */
export interface ArchiveOperatorRequest {
  name: string;
  archived: boolean;
}

/** Every mix design in the library, with whatever prices it has. */
export interface MixPriceListResponse {
  mixes: { mix_design: MixDesignOption; prices: MixDesignPrice[] }[];
}

export interface TruckingRateListResponse {
  trucking_rates: TruckingRate[];
}

export interface TaxRateListResponse {
  tax_rates: TaxRate[];
}

/* ==========================================================================
 * Entities — monthly expenses and overheads
 *
 * The other half of the P&L: what it costs to keep the company running.
 * Expenses are company-wide and never attach to a job site. See
 * docs/superpowers/specs/2026-08-26-monthly-expenses-design.md.
 * ======================================================================== */

/** 'YYYY-MM'. */
export type MonthString = string;

/**
 * 'transfer' is money moved, not money spent — a card payment, an
 * inter-account move, an owner draw. Recorded in the ledger, excluded from
 * every spending total and every chart (see the Total Breakdown design doc,
 * ruling 2 — importing both sides of a card account double-counts the same
 * money in a new place otherwise).
 */
export type ExpenseCategoryKind = 'expense' | 'transfer';

export interface ExpenseCategory {
  id: number;
  name: string;
  /** Ascending; ties break on name. */
  sort: number;
  /**
   * Hidden from the pickers, kept forever. A category an expense points at is
   * archived rather than deleted, or that expense loses the only word saying
   * what it was for.
   */
  archived: SqlBool;
  /**
   * Whether this category is a true overhead the Performance Dashboard's
   * estimate should subtract, as opposed to a job cost the Daily Reports
   * already price (materials, payroll, trucking). Defaults to 1: a wrong 0
   * makes a real cost vanish silently from the estimate, while a wrong 1
   * only makes it look worse than it should — the number that looks wrong is
   * the one that gets investigated.
   */
  is_overhead: SqlBool;
  kind: ExpenseCategoryKind;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/** Where an expense came from. 'import' is the statement-import task's. */
export type ExpenseSource = 'manual' | 'import';

export interface Expense {
  id: number;
  incurred_on: DateString;
  /** Null when nothing has been chosen, or the category was archived away. */
  category_id: number | null;
  merchant: string;
  description: string;
  /** As printed on the statement, tax included. */
  amount_total: number;
  had_gst: SqlBool;
  /** Backed out of `amount_total` server-side; never sent by a client. */
  gst_amount: number;
  amount_ex_gst: number;
  /**
   * Vestigial, like `is_overhead` on the category: stored on import and by
   * the admin form, but nothing reads it back to decide what reaches either
   * book's total — see admin-breakdown.ts's header comment.
   */
  counts_toward_total: SqlBool;
  source: ExpenseSource;
  /** The import this line arrived on. Null for everything typed by hand. */
  statement_id: number | null;
  /** Which project this spend belongs to. Null means company-wide, and is
   *  never dropped from a total. */
  job_site_id: number | null;
  fingerprint: string;
  note: string;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/** A deposit. Mirrors Expense: total as printed, split derived server-side. */
export interface Income {
  id: number;
  received_on: DateString;
  /** Who it came from, as the statement printed it. */
  source_label: string;
  description: string;
  amount_total: number;
  had_gst: SqlBool;
  gst_amount: number;
  amount_ex_gst: number;
  /** Null means company-wide, which is never dropped from a total. */
  job_site_id: number | null;
  statement_id: number | null;
  fingerprint: string;
  note: string;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/**
 * A standing monthly payment — not only an equipment lease despite the table
 * and type names (see schema.sql's comment on `equipment_lease`): insurance,
 * a phone bill, a truck payment, anything the owner pays every month, lives
 * here too. `counts_toward_total` leaves a standing cost out of the
 * Performance Dashboard for a month — a cost that has ended, or one the
 * owner wants excluded for his own reasons. It has no effect on Total
 * Breakdown or on anything imported from a bank statement; the two books
 * are never summed.
 */
export interface EquipmentLease {
  id: number;
  name: string;
  /** As invoiced, tax included when `had_gst`. */
  monthly_amount: number;
  had_gst: SqlBool;
  start_month: MonthString;
  /** Null while the lease is still running. */
  end_month: MonthString | null;
  counts_toward_total: SqlBool;
  /** What kind of standing cost this is, in the owner's own words — 'Lease' by default. */
  kind_label: string;
  note: string;
  created_at: Timestamp;
  updated_at: Timestamp;
}

/* ==========================================================================
 * API — monthly expenses and overheads
 * ======================================================================== */

/** `gst_amount` and `amount_ex_gst` are derived server-side and refused here. */
export interface SaveExpenseRequest {
  incurred_on?: DateString;
  category_id?: number | null;
  merchant?: string;
  description?: string;
  amount_total?: number;
  had_gst?: boolean;
  counts_toward_total?: boolean;
  note?: string;
}

export interface SaveLeaseRequest {
  name?: string;
  monthly_amount?: number;
  had_gst?: boolean;
  start_month?: MonthString;
  end_month?: MonthString | null;
  counts_toward_total?: boolean;
  kind_label?: string;
  note?: string;
}

export interface SaveExpenseCategoryRequest {
  name?: string;
  sort?: number;
  archived?: boolean;
  is_overhead?: SqlBool | boolean;
  kind?: ExpenseCategoryKind;
}

/** The overheads a month carries, split from the GST that is recoverable. */
export interface OverheadTotals {
  /** Ex-GST, counting only the standing monthly costs the owner has typed in
   *  and left counting toward the total — never anything from a statement. */
  overheads: number;
  /** GST paid on those same items. Recoverable, so it joins PnlSummary.gst_paid. */
  expense_gst_paid: number;
}

/** GET /api/admin/expenses?month=YYYY-MM — every lease, whatever its months. */
export interface ExpenseMonthResponse extends OverheadTotals {
  month: MonthString;
  expenses: Expense[];
  leases: EquipmentLease[];
}

export interface ExpenseCategoryListResponse {
  expense_categories: ExpenseCategory[];
}

export interface LeaseListResponse {
  leases: EquipmentLease[];
}

/* ==========================================================================
 * API — income (deposits)
 * ======================================================================== */

/** `gst_amount` and `amount_ex_gst` are derived server-side and refused here. */
export interface SaveIncomeRequest {
  received_on?: DateString;
  source_label?: string;
  description?: string;
  amount_total?: number;
  had_gst?: boolean;
  /** Null means company-wide. Absent leaves it unchanged on an edit. */
  job_site_id?: number | null;
  note?: string;
}

/** GET /api/admin/income?month=YYYY-MM */
export interface IncomeMonthResponse {
  month: MonthString;
  income: Income[];
  income_total: number;
  income_ex_gst: number;
}

/* ==========================================================================
 * Entities and API — statement import
 *
 * The browser parses the file and sends up transaction lines; the file itself
 * is never stored (spec ruling 1). A `Statement` is therefore the RECORD of an
 * import — what it was called, what it covered, how much it brought in — and
 * never the document.
 * ======================================================================== */

export type StatementKind = 'csv' | 'pdf';

/** Money out, or money in. Absent on a line means 'debit' (src/lib/statements/lines.ts). */
export type LineDirection = 'debit' | 'credit';

export interface Statement {
  id: number;
  /** The file's own name, as the owner will recognise it. */
  label: string;
  kind: StatementKind;
  /** Null when the file carried no period — a bare CSV export often does not. */
  period_start: DateString | null;
  period_end: DateString | null;
  line_count: number;
  /** What the imported lines added up to, tax included. */
  total_amount: number;
  /** A hash of the file's content, so a second import of it is noticed. */
  fingerprint: string;
  imported_at: Timestamp;
  created_at: Timestamp;
  updated_at: Timestamp;
  /**
   * Reconciliation: opening + total_credits - total_amount should equal
   * closing, within a cent (see src/lib/statements/reconcile.ts). Both
   * optional — leaving them blank skips the check rather than implying a
   * pass.
   */
  opening_balance: number | null;
  closing_balance: number | null;
  /** The deposit side; total_amount above is the debit side. */
  total_credits: number;
}

/** One line as the browser read it, before anything has been written. */
export interface StatementCandidateLine {
  incurred_on: DateString;
  merchant?: string;
  description?: string;
  /** As printed, tax included, positive. */
  amount_total: number;
  /** Money out, or money in. Absent means 'debit', so an old caller is unchanged. */
  direction?: LineDirection;
}

export interface CheckStatementRequest {
  /** The whole file's fingerprint. Absent means "do not check the file". */
  fingerprint?: string;
  lines: StatementCandidateLine[];
}

/** An expense already on the books that this (debit) line matches. */
export interface DuplicateExpense {
  expense_id: number;
  incurred_on: DateString;
  merchant: string;
  description: string;
  amount_total: number;
  source: ExpenseSource;
  statement_id: number | null;
  /** The import it arrived on, when it arrived on one. */
  statement_label: string | null;
}

/**
 * A deposit already on the books that this (credit) line matches.
 *
 * A separate shape from `DuplicateExpense`, not a reuse of it: a deposit and
 * a payment of the same amount on the same day are not each other's
 * duplicate, and the two are looked up in different tables (`income` vs
 * `expense`) so they are reported in different fields on `StatementLineCheck`.
 */
export interface DuplicateIncome {
  income_id: number;
  incurred_on: DateString;
  source_label: string;
  description: string;
  amount_total: number;
  statement_id: number | null;
  /** The import it arrived on, when it arrived on one. */
  statement_label: string | null;
}

/**
 * A standing cost this line could be the same real-world payment as —
 * surfaced so the owner can check the two records still agree with each
 * other, not because importing the line changes what either book totals.
 */
export interface LeaseClash {
  lease_id: number;
  name: string;
  monthly_amount: number;
  counts_toward_total: SqlBool;
}

export interface StatementLineCheck {
  /** Index into the request's `lines`, so the review table can match them up. */
  line: number;
  fingerprint: string;
  /** Flagged, never dropped: a genuine same-day repeat is the owner's call.
   *  Populated for a debit line; empty for a credit line. */
  duplicates: DuplicateExpense[];
  /** The deposit-side counterpart to `duplicates`. Populated for a credit
   *  line; empty for a debit line. */
  income_duplicates: DuplicateIncome[];
  /** A lease is a payment, so this is only ever checked for a debit line. */
  lease_clashes: LeaseClash[];
}

export interface CheckStatementResponse {
  /** An earlier import of this same file, when there is one. */
  already_imported: Statement | null;
  lines: StatementLineCheck[];
}

/** `gst_amount` and `amount_ex_gst` are derived server-side and refused here. */
export interface ImportStatementLine {
  incurred_on: DateString;
  /** On a debit, the merchant. On a credit, who the deposit came from —
   *  written to income.source_label. Same one column a bank statement has
   *  for it either way. */
  merchant?: string;
  description?: string;
  category_id?: number | null;
  amount_total: number;
  had_gst?: boolean;
  counts_toward_total?: boolean;
  note?: string;
  /** Money out, or money in. Absent means 'debit'. A debit lands on
   *  `expense`; a credit lands on `income`. */
  direction?: LineDirection;
  /** Which project this line belongs to. Null (the default) is company-wide,
   *  never an error. */
  job_site_id?: number | null;
}

export interface ImportStatementRequest {
  label: string;
  kind: StatementKind;
  period_start?: DateString | null;
  period_end?: DateString | null;
  fingerprint?: string;
  /** Only the lines the owner accepted on the review screen. */
  lines: ImportStatementLine[];
  /** As printed on the statement. Both optional — a bare CSV export often
   *  states neither, and either one missing skips reconciliation rather than
   *  faking a pass (src/lib/statements/reconcile.ts). */
  opening_balance?: number | null;
  closing_balance?: number | null;
}

export interface StatementImportResponse {
  statement: Statement;
  expenses: Expense[];
  income: Income[];
  expenses_created: number;
  income_created: number;
  /** Never blocks the write — see src/lib/statements/reconcile.ts. */
  reconciliation: ReconcileResult;
}

/** A statement in the list, with how much of it is still on the books. */
export interface StatementSummary extends Statement {
  expense_count: number;
  /** The deposit-side counterpart to expense_count. */
  income_count: number;
}

export interface StatementListResponse {
  statements: StatementSummary[];
}

export interface StatementDetailResponse {
  statement: Statement;
  expenses: Expense[];
  income: Income[];
}

/** One priced line. `reason` is set — and the money fields null — when unpriced. */
export interface PnlLabourLine {
  report_id: number;
  report_date: DateString;
  ticket_no: string;
  name: string;
  time_in: string;
  time_out: string;
  hours: number | null;
  rate: number | null;
  cost: number | null;
  reason: string | null;
}

export interface PnlMaterialLine {
  report_id: number;
  report_date: DateString;
  ticket_no: string;
  material: string;
  amount_text: string;
  tonnes: number | null;
  rate: number | null;
  cost: number | null;
  reason: string | null;
}

export interface PnlPourLine {
  report_id: number;
  report_date: DateString;
  ticket_no: string;
  piling_number: string;
  unit_number: string;
  amount_text: string;
  m3: number | null;
  mix_code: string;
  rate: number | null;
  revenue: number | null;
  reason: string | null;
}

/** One Daily Report extra (Standby, Delivery, Small load fee, ...), billed as REVENUE. */
export interface PnlOtherActivityLine {
  report_id: number;
  report_date: DateString;
  ticket_no: string;
  activity: string;
  quantity_text: string;
  quantity: number | null;
  unit: ActivityUnit | null;
  rate: number | null;
  revenue: number | null;
  /** The site whose own rate applied; null when the default rate did. */
  site_id: number | null;
  reason: string | null;
}

/**
 * Freight on one report: every readable delivery tonne on it, at the rate for
 * its own site, or the default rate when the site has none of its own.
 */
export interface PnlTruckingLine {
  report_id: number;
  report_date: DateString;
  ticket_no: string;
  /** The site whose own rate applied; null when the default rate did. */
  site_id: number | null;
  tonnes: number | null;
  rate: number | null;
  cost: number | null;
  reason: string | null;
}

export interface PnlSummary {
  /** Concrete sold, ex-GST, PLUS any contract revenue below — the two add. */
  revenue: number;
  /** Labour + materials ex-tax + PST on those materials + trucking ex-tax. */
  cost: number;
  margin: number;
  /**
   * Wages, ex-tax. Already folded into `cost` above — shown separately so the
   * payroll the owner is looking for is visible on its own line, not so it
   * can be added a second time. `labour + materials + cost` double-counts it.
   */
  labour: number;
  /**
   * Materials, ex-tax. Already folded into `cost` above, same as `labour` —
   * do not add it back on top of `cost`.
   */
  materials: number;
  /**
   * What SCI PAID third parties hired IN on a contract job — a rented machine
   * or a subcontractor's crew, each at its own `cost_per_day` over its own
   * days. Already folded into `cost` above, same as `labour` and `materials`.
   *
   * It has its own line because it is NOT wages. This figure used to be added
   * to `labour`, which made a card with no crew on it report "wages" for a
   * rented Pump — the total was right and the only sentence explaining it was
   * wrong by the whole hire figure (re-review #4, Important E).
   */
  hired_in: number;
  /**
   * Margin as a percentage of revenue, to one decimal. Null when there is no
   * revenue to be a percentage OF — a day with cost and no pour has no
   * meaningful margin percent, and 0% would read as breaking even.
   */
  margin_pct: number | null;
  /** Freight, ex-tax. Part of `cost`. */
  trucking: number;
  /** PST on materials. Not recoverable, so part of `cost`. */
  pst_cost: number;
  /** GST charged on concrete. Recoverable, so in neither `revenue` nor `cost`. */
  gst_collected: number;
  /** GST paid on materials and freight. Recoverable, so in neither either. */
  gst_paid: number;
  /**
   * Everything a contract report earned: equipment days, operator days, any
   * cancellation fee, AND that report's own pours (billed under the
   * contract rate, not the cubic-metre one — see admin-pnl.ts). Already
   * folded into `revenue` above, same as `labour` is into `cost` — shown
   * separately so contract money is visible on its own line, not so it can
   * be added a second time.
   */
  contract_revenue: number;
  /** Lines that could not be priced and are NOT in the figures above. */
  unpriced_count: number;
}

export interface PnlProjectSummary extends PnlSummary {
  /** For a linked group, the SITE's name; for an unlinked one, the first spelling seen. */
  project: string;
  report_count: number;
  /** null when the reports in this group are not linked to a site. */
  site_id: number | null;
  first_report_date: DateString | null;
  last_report_date: DateString | null;
  /** Distinct non-blank report dates in scope. */
  days_worked: number;
  /** margin / days_worked, null when no days. */
  margin_per_day: number | null;
}

/** A site with its all-time P&L, for the admin Sites screen. */
export interface AdminSiteRow extends SiteSummary, PnlSummary {
  first_report_date: DateString | null;
  last_report_date: DateString | null;
  days_worked: number;
  margin_per_day: number | null;
}

export interface AdminSitesResponse {
  sites: AdminSiteRow[];
}

/** Exactly one of `day` / `week` / `month` is set. `week` is the MONDAY of the week it names — the server normalises whatever date it was asked with. */
export interface PnlScope {
  day: DateString | null;
  week: DateString | null;
  month: string | null;
}

/**
 * The company-wide half of the P&L. A month carries its own overheads whole;
 * a day or a week carries its calendar SHARE of them — each day is priced at
 * `month's overheads ÷ days in that month` (calc/expenses.ts's
 * `overheadsForRange`), so a lease still never "belongs to a Tuesday", but a
 * Tuesday now shows the slice of the month it sits in and net profit tracks
 * accurately at every scope. Fields stay nullable for old responses, but the
 * server now fills them in every scope.
 */
export interface PnlOverheadFigures {
  /**
   * The scope's expenses and active leases, ex-GST — the month's total, or a
   * day/week's prorated share of it. Company-wide: it is not attributed to
   * any project, so no PnlProjectSummary carries a share of it.
   */
  overheads: number | null;
  /** Concrete margin less `overheads`. */
  net_profit: number | null;
  /**
   * `net_profit` as a percentage of revenue, to one decimal. Null when there
   * was no revenue to be a percentage OF, exactly as `margin_pct` behaves.
   */
  net_margin_pct: number | null;
}

export interface PnlDashboardResponse extends PnlOverheadFigures {
  scope: PnlScope;
  /**
   * The concrete totals. `gst_paid` also carries the GST paid on the scope's
   * overheads — recoverable exactly as it is on materials and freight — so
   * it is the projects' plus the expenses', not the projects' summed alone.
   */
  total: PnlSummary;
  projects: PnlProjectSummary[];
}

export interface PnlBreakdownResponse {
  scope: PnlScope;
  project: string;
  /** null when the group is the unlinked `?name=` one. */
  site_id: number | null;
  summary: PnlSummary;
  /** A Daily Report's personnel only. A contract day's operators are `contract_labour`, below — the two are costed by the same rule but never the same report table, so they are never merged into one array. */
  labour: PnlLabourLine[];
  materials: PnlMaterialLine[];
  trucking: PnlTruckingLine[];
  pours: PnlPourLine[];
  /** A Daily Report's Other activities, billed at the activity rates. Revenue. */
  other_activities: PnlOtherActivityLine[];
  /**
   * A contract day's operators, costed the same way `labour` is (Task 5b's
   * `priceLabourLine`) — but always empty for the unlinked `?name=` group,
   * since only a linked site can own a contract report. `report_id` on each
   * line points into `contract_report`, not `daily_report` — kept in its
   * own array, rather than merged into `labour`, so nothing has to guess
   * which table a line's id belongs to.
   */
  contract_labour: PnlLabourLine[];
  /**
   * A contract day's REVENUE lines — equipment, operators and any
   * cancellation fee — each carrying its own `reason` when it could not be
   * priced. `contractContributions` (admin-pnl.ts) only ever reads
   * `.summary` off a priced contract report, which is enough to fold the
   * money into totals but throws away WHICH name had no rate; these three
   * arrays are that same per-line detail, mirroring `contract_labour`
   * above (Task 5b's fix, applied to the revenue half — I2). Always empty
   * for the unlinked `?name=` group, same as `contract_labour`.
   */
  contract_equipment: ContractLine[];
  contract_operators: ContractLine[];
  /** 0 or 1 line per contract report in scope — see `ContractPnl.cancellation`. */
  contract_cancellation: ContractLine[];
  /**
   * What was hired in, as COST — `revenue` on each line is what SCI PAYS,
   * folded into `summary.cost`, never into `summary.revenue` (see
   * `ContractPnl.hires`). Present for the same reason the three arrays
   * above are: a hire with no `cost_per_day` counts toward
   * `summary.unpriced_count`, and a count nobody can resolve to a NAME is
   * not a warning anybody can act on. That is the common case, not the
   * exceptional one — every hire the crew records sits at cost 0 until
   * admin prices it. `/admin/pnl/project` is admin-only, so a cost
   * belonging on this wire is not an exception to the quality area's
   * no-price rule; that rule is about `/api/quality/*`.
   */
  contract_hires: ContractLine[];
  /**
   * A contract day's own pours (I1) — `contract_report_activity`, priced by
   * the same `pricePours` a Daily Report's `pours` above already are, site
   * `bills_concrete` honoured identically. Kept out of `pours` for the same
   * reason `contract_labour` is kept out of `labour`: a contract report's id
   * must never be read as a `daily_report` id.
   */
  contract_pours: PnlPourLine[];
}

/**
 * One month of what a Daily Report crew costed out — revenue, cost and
 * margin the same way `PnlSummary` defines them, bucketed by month. Feeds
 * the Tracked half of the Tracked-vs-Actual chart; see
 * `TrackedMonthsResponse`.
 */
export interface TrackedMonth {
  month: MonthString;
  revenue: number;
  cost: number;
  margin: number;
}

/** A run of months, zero-filled, for the Tracked chart. */
export interface TrackedMonthsResponse {
  months: TrackedMonth[];
}

/* ==========================================================================
 * Entities and API — Total Breakdown
 *
 * Actual dollars in and out, from imported bank statements — never from
 * pours. See src/lib/calc/breakdown.ts for why the two are kept apart.
 * ======================================================================== */

/** One category's slice of a period's spending, transfers included. */
export interface CategoryTotal {
  category_id: number | null;
  category: string;
  kind: ExpenseCategoryKind;
  total: number;
  /** Null for a transfer, and for anything when there is nothing to share. */
  share_pct: number | null;
}

/** One month's income, spend and net, for a trend line. */
export interface MonthPoint {
  month: string;
  income: number;
  spend: number;
  net: number;
}

/** One project's income, spend and net for a period. `site_id: null` is the always-present Untagged row. */
export interface SiteTotal {
  site_id: number | null;
  name: string;
  income: number;
  spend: number;
  net: number;
}

/**
 * One line of the period's ledger, exactly as it reached the bank account.
 *
 * The rollups above are the answer to "how much"; this is the answer to
 * "which ones", and the screen needs both — a category slice the owner does
 * not recognise is only useful if he can open it and read the merchants
 * inside. It rides on the same response as the rollups on purpose: a ledger
 * fetched separately could be filtered a hair differently and quietly
 * disagree with the chart sitting above it.
 *
 * `kind` is the same word the category carries, with `'income'` added for a
 * deposit: `'expense'` is money spent, `'transfer'` is money merely moved and
 * is never in any total, and `'income'` is money in.
 */
export interface BreakdownLine {
  /** Unique within its own table only — key a list on `kind` and `id` together. */
  id: number;
  kind: 'income' | ExpenseCategoryKind;
  /** `received_on` for a deposit, `incurred_on` for a payment. */
  date: DateString;
  /** The deposit's source or the payment's merchant, as the statement printed it. */
  label: string;
  description: string;
  /** Always null on a deposit: income carries no category. */
  category_id: number | null;
  /** The category's name, 'Uncategorised' for a payment with none, null on a deposit. */
  category: string | null;
  job_site_id: number | null;
  /** The job site's name, or null when the line carries no site. */
  site: string | null;
  /** As printed on the statement, tax included. */
  amount_total: number;
  amount_ex_gst: number;
  gst_amount: number;
}

/** GET /api/admin/breakdown?month=YYYY-MM|year=YYYY|span=to_date[&site=ID] */
export interface BreakdownResponse {
  /**
   * `{ to_date: true }` is a job's (or the whole company's) entire recorded
   * history — every figure on the response ignores `month`/`year` entirely
   * rather than widening around them. It is a distinct shape, not a third
   * property alongside `month`/`year`, so a screen can tell "no period was
   * asked for" apart from "this period happens to be unset".
   */
  scope: { month: MonthString } | { year: string } | { to_date: true };
  site_id: number | null;
  income_total: number;
  income_ex_gst: number;
  spend_total: number;
  spend_ex_gst: number;
  net: number;
  gst_collected: number;
  gst_paid: number;
  gst_net: number;
  by_category: CategoryTotal[];
  by_month: MonthPoint[];
  by_site: SiteTotal[];
  transfers_total: number;
  /** Share of dollars in scope carrying no job site. Null when there are none. */
  untagged_pct: number | null;
  /**
   * Every line the period holds — deposits, payments and transfers alike —
   * newest first. Scoped by the period AND by `?site=`, exactly like the
   * totals above, so the table and the headline figures can never disagree.
   */
  lines: BreakdownLine[];
}

/* ==========================================================================
 * Entities — Tracked vs Actual
 *
 * The two books, side by side, and the gap between them. See
 * src/lib/calc/compare.ts for why they are never summed. The two shapes use
 * different property names on purpose — `revenue`/`cost`/`margin` for the
 * costed book, `money_in`/`money_out`/`net` for the banked one — so a caller
 * that mixes them up reads wrong at a glance.
 * ======================================================================== */

/** What a job was costed at, from its Daily Reports, at the owner's own rates. */
export interface TrackedFigures {
  revenue: number;
  cost: number;
  margin: number;
  /** Null when there is no revenue to be a percent of. */
  margin_pct: number | null;
}

/** What actually moved through the bank account for a job. */
export interface BankedFigures {
  money_in: number;
  money_out: number;
  net: number;
}

/**
 * One job, both books side by side, and the gap between them.
 *
 * `tracked` is null when the job has no Daily Reports; `banked` is null when
 * nothing is tagged to it in the bank statements. Either gap is null whenever
 * the side it needs is missing — a job with no bank lines has not collected
 * nothing, it has nothing to compare, and 0 would read as "fully paid".
 */
export interface JobComparison {
  site_id: number | null;
  name: string;
  tracked: TrackedFigures | null;
  banked: BankedFigures | null;
  /** tracked.revenue − banked.money_in. Null unless both sides are present. */
  still_to_collect: number | null;
  /** banked.money_out − tracked.cost. Null unless both sides are present. */
  beyond_costed: number | null;
}

/* ==========================================================================
 * API — projects
 * ======================================================================== */

/** GET /api/projects */
export interface ProjectListResponse {
  projects: PublicProject[];
}

/** GET /api/project */
export interface ProjectResponse {
  project: PublicProject;
}

/** PATCH /api/project — crew only. Job defaults that never change. */
export interface UpdateProjectRequest {
  name?: string;
  client?: string;
  product?: string | null;
  sci_unit_no?: string | null;
  acceptance_point?: string | null;
  archived?: SqlBool;
}

/* ==========================================================================
 * API — job setup
 * ======================================================================== */

/** GET /api/job-setup */
export interface JobSetupResponse {
  job_setup: JobSetup;
  items: JobSetupItem[];
}

/** PATCH /api/job-setup — crew only. */
export interface UpdateJobSetupRequest {
  water_source?: string | null;
  potable_water_doc?: string | null;
  pump_line_setup?: string | null;
  acceptance_point?: string | null;
  manufacturer_confirmation?: string | null;
}

/** PATCH /api/job-setup/items/:itemId — crew only. */
export interface UpdateJobSetupItemRequest {
  status?: ItpStatus;
  initials?: string | null;
  notes?: string | null;
}

/** Response for both job-setup item and ITP item updates. */
export interface JobSetupItemResponse {
  item: JobSetupItem;
}

/* ==========================================================================
 * API — pours
 * ======================================================================== */

/** A pour plus the counts the pour list needs to show completion state. */
export interface PourSummary extends Pour {
  chute_test_count: number;
  stop_hold_event_count: number;
  photo_count: number;
  /** ITP items whose status is anything other than 'pending'. */
  itp_recorded_count: number;
  itp_total_count: number;
  end_of_pour_recorded_count: number;
  end_of_pour_total_count: number;
  /** Failed Hold points ('H' in point_type). Non-zero blocks close. */
  open_failed_hold_points: number;
}

/**
 * The whole pour record. POST /api/pours creates every child row in one
 * transaction, so none of these are null and the arrays are never empty:
 * 3 calibration trials, 2 calibration checks, 26 ITP items, 14 end-of-pour
 * items.
 */
export interface PourFull {
  pour: Pour;
  header: PourHeader;
  material_loading: MaterialLoading;
  calibration: PrePourCalibration;
  calibration_trials: CalibrationTrial[];
  calibration_checks: CalibrationCheck[];
  itp_items: ItpItem[];
  chute_tests: ChuteTest[];
  stop_hold_events: StopHoldEvent[];
  end_of_pour_items: EndOfPourItem[];
  photos: Photo[];
}

/** GET /api/pours */
export interface PourListResponse {
  pours: PourSummary[];
}

/** GET /api/pours/:pourId, POST /api/pours, PATCH /api/pours/:pourId */
export interface PourResponse {
  pour: PourFull;
}

/** POST /api/pours — crew only. */
export interface CreatePourRequest {
  label: string;
  date: DateString;
  /**
   * Copy operators, unit no, water source and calibration settings from this
   * pour ("confirm rather than retype"). Omit or null for a blank pour.
   * Chute tests, stop-hold events, photos and item statuses are NEVER copied.
   */
  prefill_from_pour_id?: number | null;
  status?: PourStatus;
}

/** Field-level patch bodies for each section of a pour. */
export type PourFieldsPatch = Partial<Pick<Pour, 'label' | 'date' | 'status'>>;

export type PourHeaderPatch = Partial<
  Omit<PourHeader, 'id' | 'pour_id' | 'created_at' | 'updated_at'>
>;

export type MaterialLoadingPatch = Partial<
  Omit<MaterialLoading, 'id' | 'pour_id' | 'created_at' | 'updated_at'>
>;

export type PrePourCalibrationPatch = Partial<
  Omit<PrePourCalibration, 'id' | 'pour_id' | 'created_at' | 'updated_at'>
>;

export interface CalibrationTrialPatch {
  /** 1..3 — identifies which trial row to patch. */
  trial_no: number;
  reading_kg?: number | null;
  counts?: number | null;
}

export interface CalibrationCheckPatch {
  /** 1..2 — identifies which check row to patch. */
  check_no: number;
  check_counts?: number | null;
  check_kg?: number | null;
  check_wt_per_count?: number | null;
  actual_counts?: number | null;
  actual_kg?: number | null;
}

/**
 * PATCH /api/pours/:pourId — crew only.
 * Send only the section that changed; this is what the client flushes on
 * section blur. Every provided section is written in one transaction and one
 * audit entry is appended per section changed.
 */
export interface UpdatePourRequest {
  pour?: PourFieldsPatch;
  header?: PourHeaderPatch;
  material_loading?: MaterialLoadingPatch;
  calibration?: PrePourCalibrationPatch;
  calibration_trials?: CalibrationTrialPatch[];
  calibration_checks?: CalibrationCheckPatch[];
}

/** POST /api/pours/:pourId/close — crew only. Body is empty. */
export interface ClosePourRequest {
  /** Optional note recorded in the audit entry. */
  note?: string | null;
}

/**
 * 200 on success. 409 with ApiErrorBody { code: 'hold_point_open',
 * blocking_items } when a failed Hold point is unresolved and not paired with
 * a stop-hold event.
 */
export type ClosePourResponse = PourResponse;

/* ==========================================================================
 * API — ITP items (6..31)
 * ======================================================================== */

/** PATCH /api/pours/:pourId/itp-items/:itemId — crew only. */
export interface UpdateItpItemRequest {
  status?: ItpStatus;
  initials?: string | null;
  notes?: string | null;
}

export interface ItpItemResponse {
  item: ItpItem;
  /**
   * True when this update just set a Hold point ('H' in point_type) to
   * 'failed'. The UI must raise the blocking prompt: stop production and
   * notify Kiewit QC before restart.
   */
  hold_point_triggered: boolean;
}

/* ==========================================================================
 * API — chute tests
 * ======================================================================== */

/** POST /api/pours/:pourId/chute-tests — crew only. */
export interface CreateChuteTestRequest {
  test_point: ChuteTestPoint;
  approx_volume_m3?: number | null;
  sample_time?: TimeString | null;
  /** inches */
  spread_in?: number | null;
  /** seconds */
  flow_seconds?: number | null;
  /** lb/ft3 */
  mud_balance?: number | null;
  grout_temp_c?: number | null;
  water_temp_c?: number | null;
  pile?: string | null;
  mix_no?: string | null;
  strength_mpa?: number | null;
  counts?: number | null;
  grout_wt_kg?: number | null;
  water_vol_l?: number | null;
  accepted?: SqlBool | null;
  tester_initials?: string | null;
  notes?: string | null;
}

/** PATCH /api/pours/:pourId/chute-tests/:testId — crew only. */
export type UpdateChuteTestRequest = Partial<CreateChuteTestRequest>;

export interface ChuteTestResponse {
  chute_test: ChuteTest;
  computed: ChuteTestComputed;
}

export interface ChuteTestListResponse {
  chute_tests: ChuteTest[];
}

/* ==========================================================================
 * API — stop-hold events
 * ======================================================================== */

/** POST /api/pours/:pourId/stop-hold-events — crew only. */
export interface CreateStopHoldEventRequest {
  time_stopped?: TimeString | null;
  reason?: string | null;
  corrective_action?: string | null;
  additional_test_required?: SqlBool | null;
  restart_time?: TimeString | null;
  qc_acceptance_notes?: string | null;
}

/** PATCH /api/pours/:pourId/stop-hold-events/:eventId — crew only. */
export type UpdateStopHoldEventRequest = Partial<CreateStopHoldEventRequest>;

export interface StopHoldEventResponse {
  stop_hold_event: StopHoldEvent;
}

export interface StopHoldEventListResponse {
  stop_hold_events: StopHoldEvent[];
}

/* ==========================================================================
 * API — end-of-pour items
 * ======================================================================== */

/** PATCH /api/pours/:pourId/end-of-pour-items/:itemId — crew only. */
export interface UpdateEndOfPourItemRequest {
  status?: EndOfPourStatus;
  notes?: string | null;
  initials?: string | null;
}

export interface EndOfPourItemResponse {
  item: EndOfPourItem;
}

export interface EndOfPourItemListResponse {
  items: EndOfPourItem[];
}

/* ==========================================================================
 * API — photos
 * ======================================================================== */

/** Long edge in px the phone downscales to before upload. */
export const PHOTO_MAX_EDGE_PX = 1600;
/** JPEG quality used for the on-device downscale. */
export const PHOTO_JPEG_QUALITY = 0.8;
/** Hard server-side cap on an uploaded object. */
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

/**
 * POST /api/pours/:pourId/photos/upload-url — crew only.
 * Returns a short-lived signed URL the phone PUTs the JPEG straight to.
 */
export interface PhotoUploadUrlRequest {
  content_type: string;
  bytes: number;
  /**
   * SHA-256 of the downscaled bytes about to be PUT, hex-encoded (64 chars).
   * REQUIRED — the route refuses a grant request without one (validation_failed,
   * not a silent non-deduping fallback). It is what lets the server derive
   * the R2 key from content instead of a random id, so uploading the same
   * image twice to the same pour collapses to one photo. This is what a
   * stranded, pre-existing queue entry gets too: src/lib/photo.ts's
   * `attempt()` computes it from the downscaled Blob already in the queue's
   * IndexedDB record at flush time, never from a field persisted at enqueue
   * time, so every queued photo — however old — has one the moment it
   * actually uploads. See src/api/photos.ts (buildPhotoKey).
   */
  content_hash: string;
  section?: PhotoSection | null;
  row_id?: number | null;
}

export interface PhotoUploadUrlResponse {
  /** Absolute URL to PUT the image bytes to. */
  upload_url: string;
  /** Opaque R2 object key to send back to the attach route. */
  r2_key: string;
  /** HTTP method to use for the upload. */
  method: 'PUT';
  /** Headers that must be sent with the upload request. */
  headers: Record<string, string>;
  expires_at: Timestamp;
}

/** POST /api/pours/:pourId/photos — crew only. Attach an uploaded object. */
export interface AttachPhotoRequest {
  r2_key: string;
  section?: PhotoSection | null;
  row_id?: number | null;
  caption?: string | null;
  taken_at?: Timestamp | null;
  width?: number | null;
  height?: number | null;
  bytes?: number | null;
}

export interface PhotoResponse {
  photo: Photo;
}

export interface PhotoListResponse {
  photos: Photo[];
}

/* ==========================================================================
 * API — export
 * ======================================================================== */

/**
 * GET /api/pours/:pourId/export.csv  -> text/csv
 * GET /api/export/pours.csv          -> text/csv (all pours in the project)
 * GET /api/pours/:pourId/export.pdf  -> application/pdf
 *
 * All three are available to crew AND client. They stream a file with a
 * Content-Disposition attachment filename; there is no JSON response body.
 * The single-pour CSV emits the header/material/calibration block first, then
 * the chute test table, then the stop-hold table, then the ITP and
 * end-of-pour tables, each preceded by a section title row. The PDF prints the
 * same sections in the same order, drawn to the print stylesheet.
 */
export interface ExportFileMeta {
  filename: string;
  content_type: 'text/csv' | 'application/pdf';
}

/* ==========================================================================
 * API — share tokens
 * ======================================================================== */

/** Share token as returned to the crew, with the ready-to-send link. */
export interface ShareTokenView extends ShareToken {
  /** Absolute URL, e.g. 'https://<host>/share/KiewitWF/<token>'. */
  share_url: string;
  active: boolean;
}

export interface ShareTokenListResponse {
  share_tokens: ShareTokenView[];
}

/** POST /api/share-tokens — crew only. */
export interface CreateShareTokenRequest {
  /** Who it is being issued to. */
  label: string;
}

export interface ShareTokenResponse {
  share_token: ShareTokenView;
}

/** POST /api/share-tokens/:tokenId/revoke — crew only. Body is empty. */
export type RevokeShareTokenResponse = ShareTokenResponse;

/* ==========================================================================
 * API — audit log (read-only, both roles)
 * ======================================================================== */

/** GET /api/audit?pour_id=<n>&limit=<n> */
export interface AuditListResponse {
  entries: AuditEntry[];
}

/* ==========================================================================
 * Worker environment bindings
 * ======================================================================== */

/**
 * Cloudflare bindings, as declared in wrangler.jsonc. Typed loosely here so
 * this file stays importable from the browser build; the API layer casts to
 * D1Database / R2Bucket from @cloudflare/workers-types.
 */
export interface Env {
  DB: unknown;
  PHOTOS: unknown;
  ASSETS: unknown;
  SESSION_SECRET: string;
  APP_NAME: string;
  ENVIRONMENT: string;
}
